<template>
  <div class="new-main-page">
    <h1 class="new-main-header">
      <img
        :src="theme.current.headerImage"
        alt="AI Logo"
        class="header-image"
      />
      <!-- Header control card: Voice Mode toggle + demo-only End session.
           Shown only while an active current-chat session is in view. -->
      <div
        class="header-controls"
        v-if="showVoiceToggle || showEndControl"
      >
        <!-- Voice Mode row: label on the left, toggle on the right -->
        <div class="voice-mode-toggle" v-if="showVoiceToggle">
          <div class="voice-label-row">
            <label class="voice-toggle-label">Voice Mode</label>
            <button
              v-if="!isVoiceCompatibleBrowser"
              class="voice-browser-tooltip-btn"
              @click.stop="showBrowserAlert"
              aria-label="Browser compatibility info"
            >
              <i class="bi bi-info-circle-fill"></i>
            </button>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              v-model="voiceMode"
              @change="toggleVoiceMode"
              :disabled="
                waiting ||
                recording ||
                audioInteractionLocked ||
                !isVoiceCompatibleBrowser
              "
            />
            <span class="slider round"></span>
          </label>
        </div>

        <!-- Divider only when both rows are present -->
        <div
          class="control-divider"
          v-if="showVoiceToggle && showEndControl"
        ></div>

        <!-- Demo-only: force-close the session and start a new one -->
        <button
          v-if="showEndControl"
          type="button"
          class="end-session-button"
          @click="forceEndSession"
          :disabled="forceEnding"
          title="Demo: close this session and start a new one"
        >
          <i class="bi bi-power" aria-hidden="true"></i>
          <span>{{ forceEnding ? "Ending…" : "End session" }}</span>
        </button>
      </div>
    </h1>

    <div class="chat-panel">
    <div v-if="loading" class="loading-message">Session loading...</div>
    <div v-else class="chat-history" ref="historyRef">
      <div v-if="!session.isViewingCurrentChat">
        <h4>Past Coaching Session with {{ session.chatbotName }}</h4>
        <p
          class="chat-summary-display"
          v-html="renderMarkdown(session.selectedSummary)"
        />
      </div>
      <h4 v-if="!session.isViewingCurrentChat">Conversation Record</h4>
      <!-- Survey block -->
      <div v-if="surveyRequired">
        <div class="chat-summary-display summary-scroll">
          <ul class="chat-message">
            <li class="response">
              <div class="message-header">
                <img class="coach-icon" :src="theme.current.teamLogo" />
                <p class="message-sender">UMN Research Team</p>
              </div>
              <div class="message-content">
                <div class="message-text">
                  <span
                    class="text"
                    v-html="
                      renderMarkdown(
                        `Please fill out this questionnaire before starting the session!\n${session.surveyLink}`,
                      )
                    "
                  />
                </div>
              </div>
            </li>
          </ul>
          <div v-if="surveyRequired" class="survey-ack">
            <button type="button" class="survey-ack-btn" @click="ackSurvey">
              Questionnaire completed
            </button>
            <div class="survey-ack-hint">
              Please click after completing the questionnaire (opens in a new
              tab).
            </div>
          </div>
        </div>
      </div>

      <!-- div class="chat-window" v-if="displayedChatHistory.length" -->
      <!-- Case 1: Show chat window when we have chat history and don't need a new session -->
      <div
        v-if="displayedChatHistory.length && !surveyRequired"
        class="chat-window"
      >
        <ul class="chat-message">
          <li
            v-for="message in displayedChatHistory"
            :key="message.id"
            :class="[
              message.isResponse ? 'response' : 'text-input',
              isSystemNotice(message) ? 'system-notice' : '',
            ]"
          >
            <div class="message-header">
              <!--
                SYSTEM. A turn the server could not answer. It keeps the bubble
                layout so it inherits spacing and contrast that already work in
                both themes, but it is attributed to the platform rather than to
                the coach: an infrastructure failure in her voice would read as
                something she said.
              -->
              <i
                v-if="isSystemNotice(message)"
                class="bi bi-gear-fill system-icon"
                aria-hidden="true"
              ></i>
              <!-- USER profile -->
              <img
                v-else-if="!message.isResponse && session.profilePhoto"
                :src="session.profilePhoto"
                alt="Profile Photo"
                class="profile-photo"
              />
              <i
                v-else-if="!message.isResponse"
                class="bi bi-person-circle"
                aria-hidden="true"
              ></i>
              <!-- COACH profile -->
              <img
                v-else
                :src="theme.current.coachImage"
                alt="Coach"
                class="coach-icon"
              />
              <p class="message-sender">
                <!-- Use real user name -->
                {{
                  isSystemNotice(message)
                    ? "System"
                    : message.isResponse
                    ? session.chatbotName
                    : `${capitalize(session.firstname)} ${capitalize(
                        session.lastname,
                      )}`
                }}
              </p>
            </div>
            <div class="message-content">
              <span class="timestamp">{{
                formatTimestamp(message.timestamp)
              }}</span>
              <div class="message-text">
                <span class="text" v-html="renderMarkdown(displayText(message))" />
              </div>
              <!-- Bubble action buttons (copy + audio placeholder). Not offered
                   on a system notice: there is nothing to copy or read aloud. -->
              <div
                v-if="!isSystemNotice(message)"
                class="bubble-actions"
                aria-label="Message actions"
              >
                <button
                  type="button"
                  class="bubble-action-btn"
                  :title="copiedMap?.[message.id] ? 'Copied!' : 'Copy'"
                  @click="copyMessageText(message)"
                >
                  <!-- Convert to green check and get back to copy -->
                  <i
                    v-if="copiedMap?.[message.id]"
                    class="bi bi-check2"
                    aria-hidden="true"
                  />
                  <i v-else class="bi bi-clipboard" aria-hidden="true" />
                </button>

                <!-- Only visible when voice mode toggle is displayed -->
                <button
                  v-if="voiceMode"
                  type="button"
                  class="bubble-action-btn"
                  :disabled="
                    // If THIS message is currently playing, allow click (so user can pause)
                    !(audioStateByMid?.[message.id] === 'playing') &&
                    // otherwise lock all play buttons while any audio is loading/playing/streaming
                    audioInteractionLocked
                  "
                  :title="
                    isAudioBusy && playingMid !== message.id
                      ? 'Another audio is playing'
                      : audioStateByMid?.[message.id] === 'playing'
                      ? 'Pause audio'
                      : audioStateByMid?.[message.id] === 'loading'
                      ? 'Loading audio...'
                      : 'Play audio'
                  "
                  @click="playAudio(message)"
                >
                  <i
                    v-if="audioStateByMid?.[message.id] === 'loading'"
                    class="bi bi-hourglass-split"
                    aria-hidden="true"
                  />
                  <i
                    v-else-if="audioStateByMid?.[message.id] === 'playing'"
                    class="bi bi-pause-fill"
                    aria-hidden="true"
                  />
                  <i v-else class="bi bi-volume-up" aria-hidden="true" />
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <!-- Case 2: Show create session button when newSessionNeeded is true -->
      <div
        v-else-if="
          newSessionNeeded && session.isViewingCurrentChat && !surveyRequired
        "
        class="create-session-container"
      >
        <div class="create-session-content">
          <!-- h3>New Week - Start a New Coaching Session</h3>
          <p>
            Welcome to a new week! Create a new coaching session to continue
            your progress.
          </p -->

          <!--
            While the previous period's summaries are still being produced the
            button is replaced rather than disabled. The session genuinely
            cannot be created yet -- its welcome is built from those summaries,
            and starting early would lose the participant's continuity
            permanently -- so offering a control that refuses would be a worse
            answer than saying why.
          -->
          <div v-if="maintenanceNewSessionBlocked" class="maintenance-pending">
            <i class="bi bi-hourglass-split" aria-hidden="true"></i>
            <p>
              Your next session is being prepared. It will be available once
              tonight's summaries are ready.
            </p>
            <button
              type="button"
              class="maintenance-retry"
              @click="recheckMaintenance"
              :disabled="maintenanceChecking"
            >
              {{ maintenanceChecking ? "Checking ..." : "Check again" }}
            </button>
          </div>

          <div v-else class="centered-button-container">
            <button @click="createNewSession" class="create-session-button">
              Begin a new session!
            </button>
          </div>
        </div>
      </div>

      <!-- Case 3: Show loading indicator while loading welcome text -->
      <div v-else-if="loading" class="loading-container">
        <p>Loading your coaching session...</p>
      </div>

      <!-- Case 4: Show empty chat window ready for input when session created but no messages yet -->
      <div
        v-else-if="
          !newSessionNeeded && session.isViewingCurrentChat && !surveyRequired
        "
        class="chat-window empty-chat"
      >
        <p class="welcome-prompt">
          Type a message to start your coaching session.
        </p>
      </div>

      <!-- Case 5: Show empty state when no chat history is selected -->
      <div v-else-if="!surveyRequired" class="empty-chat-container">
        <p>Select a session from the sidebar to view the conversation.</p>
      </div>
      <!--div v-else>
        <p>No chat history selected.</p>
      </div-->
    </div>

      <!-- Demo-only: translucent overlay while the session summary generates.
           The input is hidden then, so this is the primary progress signal. -->
      <div
        v-if="forceEnding && session.isViewingCurrentChat"
        class="summarizing-overlay"
      >
        <div class="summarizing-card">
          <img class="summarizing-spinner" src="../assets/site-wide/loading.svg" alt="" />
          <p class="summarizing-note">Saving your session summary…</p>
        </div>
      </div>
    </div>

    <!-- Demo-only: summaries not ready yet after a force-end -->
    <div
      v-if="summaryPending && !newSessionNeeded && session.isViewingCurrentChat"
      class="summary-pending-bar"
    >
      <p class="summary-pending-note">
        <i class="bi bi-hourglass-split" aria-hidden="true"></i>
        Session summaries are still processing.
      </p>
      <button
        type="button"
        class="start-anyway-button"
        @click="createNewSession"
      >
        Start anyway
      </button>
    </div>

    <!--
      Durable state for an outage. The popup fires once and is dismissible, so
      this is what keeps the condition visible. The composer stays enabled on
      purpose: retrying is currently the only way to find out the server is
      back, so locking it would strand the participant.
    -->
    <div
      class="server-down-banner"
      role="status"
      v-if="
        serverDown &&
        session.isViewingCurrentChat &&
        !isChatEnded &&
        !heldByMaintenance &&
        !surveyRequired
      "
    >
      <span class="server-down-banner__dot"></span>
      <span>
        {{ session.chatbotName }} is temporarily unavailable, so your last
        message was not delivered. Please try again in a few minutes.
      </span>
    </div>

    <!--
      MAINTENANCE. Two states, and they are different situations with different
      resolutions, so they get different text.

      Approaching: a plain statement of when this session closes, rendered from
      the server's instant into the participant's own clock. Deliberately NOT a
      countdown -- a clock ticking down for an hour manufactures urgency out of
      a routine nightly job, and answering "when do I need to wrap up" needs no
      timer and no polling.

      In progress: the composer is locked and the only affordance is to check
      again, because release happens when the batch finishes rather than at a
      time anyone can predict.
    -->
    <div
      class="maintenance-banner"
      :class="{ 'maintenance-banner--blocked': maintenanceBlocked }"
      role="status"
      aria-live="polite"
      v-if="
        (maintenanceBlocked || maintenanceClosingSoon) &&
        session.isViewingCurrentChat &&
        !isChatEnded &&
        !heldByMaintenance &&
        !surveyRequired
      "
    >
      <i
        class="bi"
        :class="maintenanceBlocked ? 'bi-hourglass-split' : 'bi-clock'"
        aria-hidden="true"
      ></i>
      <span>{{ maintenanceMessage }}</span>
      <button
        v-if="maintenanceBlocked"
        type="button"
        class="maintenance-retry"
        @click="recheckMaintenance"
        :disabled="maintenanceChecking"
      >
        {{ maintenanceChecking ? "Checking ..." : "Check again" }}
      </button>
    </div>

    <div
      class="text-prompt"
      :class="{
        'text-prompt--voice': !isDesktop && voiceMode,
        'text-prompt--under-banner': bannerAbove,
      }"
      v-if="
        session.isViewingCurrentChat &&
        !isChatEnded &&
        !heldByMaintenance &&
        !surveyRequired
      "
    >
      <!-- Text Input (disabled in voice mode) -->
      <textarea
        ref="textareaRef"
        v-show="isDesktop || !voiceMode"
        v-model="textInput"
        @keydown.ctrl.enter.prevent="!voiceMode && sendMessage()"
        @keydown.meta.enter.prevent="!voiceMode && sendMessage()"
        :disabled="waiting || voiceMode || maintenanceBlocked"
        :placeholder="
          maintenanceBlocked
            ? 'Nightly maintenance in progress ...'
            : voiceMode
            ? 'Voice mode active'
            : waitingLong
            ? `${session.chatbotName} is still thinking — thanks for your patience ...`
            : waiting
            ? `${session.chatbotName} is thinking ...`
            : `Message ${session.chatbotName} ...`
        "
        :class="{ 'voice-mode-disabled': voiceMode }"
      ></textarea>

      <div class="text-prompt-controls">
        <!-- Regular Send Button (text mode) -->
        <button
          v-if="!voiceMode && !waiting"
          type="button"
          @click="sendMessage"
          class="send-button"
          :disabled="maintenanceBlocked"
        >
          Send
        </button>

        <!-- Waiting Spinner -->
        <button
          v-if="!voiceMode && waiting"
          type="button"
          class="waiting-button"
          disabled
        >
          <img class="svg" src="../assets/site-wide/loading.svg" />
        </button>

        <!-- Push-to-Talk Button (voice mode) -->
        <button
          v-if="voiceMode"
          type="button"
          class="push-to-talk-button"
          :class="{ recording: recording }"
          @pointerdown.prevent="handlePTTDown"
          @contextmenu.prevent
          :disabled="
            waiting ||
            !speechSupported ||
            audioInteractionLocked ||
            maintenanceBlocked
          "
          :title="
            !speechSupported
              ? 'Speech recognition not supported'
              : isAnyAudioLoading || isAudioBusy
              ? 'Audio is loading/playing — recording is temporarily disabled'
              : 'Hold to record, release to send'
          "
        >
          <div class="record-button-content">
            <div class="record-circle" :class="{ active: recording }">
              <div class="record-dot"></div>
            </div>
            <span class="record-text">
              {{ recording ? "Recording..." : "REC" }}
            </span>
          </div>
        </button>
        <div class="text-prompt-note">
          {{ voiceMode ? "Push to talk" : "Ctrl-Enter" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
import chatApi from "@/services/chat_api";
import debugApi from "@/services/debug_api";
import { apiClient } from "@/services/api_client";
import { RealtimeChatClient } from "@/services/realtime_ws";
import { TextChatClient } from "@/services/text_chat_ws";
import {
  onMounted,
  nextTick,
  computed,
  ref,
  onBeforeUnmount,
  watch,
} from "vue";
import { useSessionStore } from "@/stores/session";
import { useThemeStore } from "@/stores/theme";
import { format } from "date-fns";
import swal from "@/services/swal";

export default {
  name: "ChatPage",
  setup() {
    const session = useSessionStore();
    const loading = ref(true);
    const waiting = ref(false);
    // Set once a turn has been "thinking" long enough that the default
    // placeholder starts to look stalled rather than in-progress -- e.g. the
    // inference broker auto-scaling a burst AWS instance under heavy load
    // (measured p50 TTFT climbs from ~0.4s at light load to ~7.3s at 200
    // concurrent users; an actual scale-out event can run well past that,
    // per David's "could take 30 sec" estimate in #capacity-planning).
    // 10s sits above ordinary high-load latency and below the point Zach
    // flagged as genuinely bothersome, so it should only fire for the
    // scaling case this is meant to cover -- revisit against real broker
    // timing data once it's available.
    const waitingLong = ref(false);
    let slowResponseTimer = null;
    const newSessionNeeded = ref(false);
    // Demo-only force-end flow (enable_session_manipulation accounts):
    // forceEnding = request in flight; summaryPending = summaries not ready yet.
    const forceEnding = ref(false);
    const summaryPending = ref(false);
    const textInput = ref("");
    const textareaRef = ref(null);
    // Clicking Send moves focus to the button; nothing moved it back, so every
    // reply meant a manual click back into the textarea before typing the next
    // message. A watch rather than a call inside sendMessage(), because
    // `waiting` flips false from over a dozen different sites (success,
    // errors, voice, force-end...) and this needs to fire after all of them,
    // not just the happy path. Skipped when the field is disabled for another
    // reason (voice mode, maintenance) -- focus() on a disabled element is
    // already a no-op in every browser, so no extra guard is needed here.
    //
    // Desktop only. Refocusing pops the virtual keyboard on mobile -- welcome
    // right after a message the participant just sent, but intrusive if a
    // reply lands while they'd stepped away from the keyboard. isDesktop is
    // the same flag the voice-mode layout already keys off of.
    watch(waiting, (isWaiting, wasWaiting) => {
      if (wasWaiting && !isWaiting && isDesktop.value) {
        nextTick(() => textareaRef.value?.focus());
      }
      // Same "fires from a dozen sites" reasoning as the focus() logic above:
      // hook the timer to the ref itself rather than to sendMessage()/
      // sendVoiceMessage(), so it starts and clears correctly regardless of
      // which of those call sites flips `waiting`.
      if (isWaiting && !wasWaiting) {
        if (slowResponseTimer) clearTimeout(slowResponseTimer);
        slowResponseTimer = setTimeout(() => {
          waitingLong.value = true;
          slowResponseTimer = null;
        }, 10000);
      } else if (!isWaiting && wasWaiting) {
        if (slowResponseTimer) {
          clearTimeout(slowResponseTimer);
          slowResponseTimer = null;
        }
        waitingLong.value = false;
      }
    });
    // --- Nightly maintenance window ---------------------------------------
    //
    // Everything below reads the server's instants out of the store. Nothing
    // computes the window client-side: it is defined in America/Chicago and the
    // browser's clock and zone are both unreliable, so the server sends
    // absolute instants and the browser only formats them.
    //
    // maintenanceBlockedLive is a local override for the case the store cannot
    // see: the socket refusing a turn mid-conversation. The page flips into the
    // blocked state from that frame rather than waiting for a refetch.
    const maintenanceBlockedLive = ref(false);
    const maintenanceChecking = ref(false);
    // How long before the window a heads-up appears. An hour is enough to wrap
    // up a conversation and short enough that it is not background noise.
    const MAINTENANCE_NOTICE_MINUTES = 60;

    const historyRef = ref(null);
    const copiedMap = ref({}); // { [messageId]: true/false }
    const copiedTimers = new Map(); // messageId -> timeout id
    const isAdminView = ref(false);
    const isMilitaryGroup = ref(false);
    const isDesktop = ref(false);
    const isVoiceCompatibleBrowser = ref(false);
    const showBrowserAlert = () =>
      swal.fire({
        icon: "warning",
        text: "Voice mode requires Chrome, Safari, or Samsung Internet.",
      });
    let mql = null;

    const theme = useThemeStore();

    // Voice Mode state
    const voiceMode = ref(false);
    const recording = ref(false);
    const speechSupported = ref(false);
    const pointerId = ref(null);
    const isStarting = ref(false);
    const isHoldingPTT = ref(false);
    // The inference server is unreachable. Set from a typed `error` event,
    // cleared the moment a turn starts succeeding again. Deliberately does NOT
    // disable the composer: with no status endpoint yet, a retry is the only
    // way to discover that the server came back, so locking the input would be
    // a dead end.
    const serverDown = ref(false);
    const serverDownNotified = ref(false);
    const assistantStream = new Map();
    // Events keyed to a single assistant turn: without a message_id there is no
    // stream to attach them to, so they are dropped. Connection-level events
    // (session_ended, error) must NOT be listed here. Dropping those leaves the
    // page stuck on `waiting` with no way to recover.
    const PER_MESSAGE_WS_EVENTS = new Set([
      "assistant_started",
      "assistant_text_delta",
      "assistant_text_done",
      "assistant_done",
      "assistant_persisted",
      "user_persisted",
      "assistant_audio_delta",
      "assistant_audio_done",
      "assistant_audio_transcript_delta",
      "assistant_audio_transcript_done",
    ]);
    const textTokenQueues = new Map();    // mid -> string[] of pending delta tokens (drip-feed)
    const textDrainRafs = new Map();      // mid -> rAF id
    const textDrainLastTime = new Map();  // mid -> timestamp of last token reveal
    const DRAIN_MS_PER_TOKEN = 100;           // matches backend 20ms sleep; burst fallback ~50 tokens/sec
    const audioCtx = ref(null);
    const audioNextTime = ref(0); // schedule cursor
    const audioInputSampleRate = 24000; // PCM @ 24k
    const activeAudioSources = new Set(); // Set<AudioBufferSourceNode>
    const suppressAssistantAudio = ref(false); // when true, ignore incoming
    const finalTranscriptBuf = ref("");
    let pcmRemainder = new Uint8Array(0); // for odd-byte chunk boundaries
    let recognizer = null;
    let lastProcessedIndex = -1;
    let interimTranscriptBuf = "";
    let realtime = null;
    let textChat = null;
    let scrollQueued = false;

    // Text streaming pacing
    const textFlushTimers = new Map(); // mid -> timer id
    const textPending = new Map(); // mid -> latest text to render
    const textChain = new Map(); // mid -> Promise chain (keeps order)

    // tweak these:
    const TEXT_FLUSH_MS = 250; // render cadence (throttle)
    const TEXT_AUDIO_LEAD_MS = 400; // let audio stay ahead by ~250ms
    const TEXT_MAX_DELAY_MS = 400; // safety cap (don't lag forever)

    const playingMid = ref(null); // message.id (message_id in voice, chat_id in text)
    const audioEl = ref(null); // single audio element
    const audioUrlByChatId = new Map(); // chatId -> objectURL
    const audioStateByMid = ref({}); // { [message.id]: "idle" | "loading" | "playing" }
    const assistantSpeaking = ref(false);
    let speakIdleTimer = null;

    // User audio recording (MediaRecorder)
    const mediaStream = ref(null);
    const mediaRecorder = ref(null);
    const mediaChunks = ref([]);
    // turn correlation
    const currentVoiceTurnId = ref(null);
    // hold recorded blobs until backend tells us user chat_id
    const pendingUserAudioByTurnId = new Map(); // turnId -> { blob, contentType }

    const updateIsDesktop = () => {
      isDesktop.value = !!mql?.matches;
    };

    const isAudioBusy = computed(() => {
      // purely reactive: if any message is marked "playing", we are busy
      const s = audioStateByMid.value || {};
      return Object.values(s).some((v) => v === "playing");
    });

    const isAnyAudioLoading = computed(() => {
      const s = audioStateByMid.value || {};
      return Object.values(s).some((v) => v === "loading");
    });

    const setAudioState = (mid, state) => {
      audioStateByMid.value = { ...audioStateByMid.value, [mid]: state };
    };

    const resetPlaybackState = (midToIdle = null) => {
      if (midToIdle) setAudioState(midToIdle, "idle");
      playingMid.value = null;
    };

    const setAssistantSpeaking = (v) => {
      assistantSpeaking.value = v;
      if (!v) return;
      // keep it true while audio keeps arriving / scheduling happens
      if (speakIdleTimer) clearTimeout(speakIdleTimer);
      speakIdleTimer = setTimeout(() => {
        // if no new audio arrived recently, assume speaking ended
        assistantSpeaking.value = false;
      }, 450); // tweak 300~600ms if needed
    };

    const audioInteractionLocked = computed(() => {
      // lock if assistant is speaking, or any audio is loading/playing
      return (
        assistantSpeaking.value || isAnyAudioLoading.value || isAudioBusy.value
      );
    });

    const resolveChatIdForMessage = (message) => {
      // Voice mode: message.id is message_id; chat_id comes from assistant_persisted
      // Text mode: message.id is already chat_id
      const cid = message?.chat_id ?? message?.chatId ?? message?.id;
      const n = Number(cid);
      return Number.isFinite(n) ? n : null;
    };

    const attachChatIdToMessage = (messageId, chatId) => {
      const lists = [session.currentChatHistory, session.selectedChatHistory];
      for (const arr of lists) {
        const m = arr?.find?.((x) => x?.id === messageId);
        if (m) {
          // make it explicit for later playback
          m.chat_id = chatId;
        }
      }
    };

    const ensureMicStream = async () => {
      if (mediaStream.value) return mediaStream.value;
      mediaStream.value = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      return mediaStream.value;
    };

    const startUserAudioRecording = async () => {
      const stream = await ensureMicStream();
      mediaChunks.value = [];

      // Capture turnId now (at recording start) to avoid a race where
      // recognizer.onend clears currentVoiceTurnId before mr.onstop fires.
      const capturedTurnId = currentVoiceTurnId.value;

      const mr = new MediaRecorder(stream);
      mediaRecorder.value = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) mediaChunks.value.push(e.data);
      };

      mr.onstop = () => {
        const actualType = mr.mimeType || "audio/webm";
        const blob = new Blob(mediaChunks.value, { type: actualType });
        console.log("User audio blob:", blob.size, "bytes, type:", actualType);
        if (capturedTurnId && blob.size > 0) {
          pendingUserAudioByTurnId.set(capturedTurnId, {
            blob,
            contentType: actualType,
          });
        }
      };

      mr.start();
    };

    const stopUserAudioRecording = () => {
      try {
        if (mediaRecorder.value && mediaRecorder.value.state !== "inactive") {
          mediaRecorder.value.stop();
        }
      } catch (e) {
        console.warn("Failed to stop MediaRecorder", e);
      }
    };

    const teardownVoiceMode = (reason = "toggle_off") => {
      console.log(`ChatPage.vue, teardownVoiceMode, reason: ${reason}`);
      try {
        // Stop SR if active
        if (recognizer) {
          try {
            recognizer.stop();
          } catch {
            console.warn("Unable to stop SR");
          }
        }
      } catch {
        console.warn("Unable to stop SR");
      }

      // Reset PTT / SR flags
      recording.value = false;
      isHoldingPTT.value = false;
      pointerId.value = null;
      isStarting.value = false;

      // Stop assistant audio + allow audio again
      suppressAssistantAudio.value = false;
      stopAssistantAudio();

      // Reset assistant speaking indicator
      assistantSpeaking.value = false;
      if (speakIdleTimer) {
        clearTimeout(speakIdleTimer);
        speakIdleTimer = null;
      }

      // Cancel any pending text flush timers / buffers
      for (const t of textFlushTimers.values()) clearTimeout(t);
      textFlushTimers.clear();
      textPending.clear();
      textChain.clear();

      // Cancel text drip-feed drains
      for (const id of textDrainRafs.values()) cancelAnimationFrame(id);
      textDrainRafs.clear();
      textTokenQueues.clear();
      textDrainLastTime.clear();

      // Clear current streaming state
      assistantStream.clear();

      // Clear odd-byte remainder
      pcmRemainder = new Uint8Array(0);

      // Close realtime WS (IMPORTANT: use the closure variable, not this.realtime)
      if (realtime) {
        try {
          realtime.close();
        } catch (e) {
          console.warn("Realtime close failed", e);
        }
        realtime = null;
      }

      // (Optional) if you want to fully release AudioContext resources:
      if (audioCtx.value) {
        try {
          audioCtx.value.close();
        } catch {
          console.warn("Unable to release AudioContext");
        }
        audioCtx.value = null;
      }
      audioNextTime.value = 0;

      watch(isDesktop, (desktop) => {
        // If user transitions to mobile width while voice mode is ON, shut it down safely
        if (
          !desktop &&
          voiceMode.value &&
          !isAdminView.value &&
          !isMilitaryGroup.value
        ) {
          voiceMode.value = false;
          teardownVoiceMode("desktop_to_mobile");
        }
      });
    };

    const stopPlayback = () => {
      const mid = playingMid.value;

      try {
        if (audioEl.value) {
          audioEl.value.pause();
          audioEl.value.currentTime = 0;
        }
      } catch {
        console.warn("Can't stop playing the audio");
      }

      resetPlaybackState(mid);
    };

    const playAudio = async (message) => {
      if (!message) return;

      const mid = message.id;
      const chatId = resolveChatIdForMessage(message);
      if (!chatId) return;

      const state = audioStateByMid.value?.[mid] || "idle";

      // If THIS message is currently playing -> pause (toggle)
      if (state === "playing" && playingMid.value === mid) {
        try {
          audioEl.value?.pause();
        } catch {
          console.warn("Audio pause doesn't work");
        }
        resetPlaybackState(mid);
        return;
      }

      // If this message is already loading, ignore spam clicks
      if (state === "loading") return;

      // If any other audio is busy/loading, ignore clicks (UI already disables, but this is safety)
      if (audioInteractionLocked.value && playingMid.value !== mid) return;

      // Stop whatever might be active (clears reactive state)
      stopPlayback();

      setAudioState(mid, "loading");
      playingMid.value = mid;

      try {
        let url = audioUrlByChatId.get(chatId);
        if (!url) {
          const { blob, contentType } = await chatApi.getChatAudio(chatId);
          const typedBlob = blob.type
            ? blob
            : new Blob([blob], { type: contentType || "audio/wav" });
          url = URL.createObjectURL(typedBlob);
          audioUrlByChatId.set(chatId, url);
        }

        const el = ensureAudioEl();
        el.src = url;
        el.currentTime = 0;

        await el.play();
        setAudioState(mid, "playing");
      } catch (e) {
        console.error("Audio playback failed:", e);

        // If backend says "no audio for this chat", show friendly alert
        const status = e?.response?.status;
        if (status === 404 || status === 409 || status === 422) {
          swal.fire({
            icon: "warning",
            text: "No audio available for this chat",
          });
        }
        resetPlaybackState(mid);
      }
    };

    const copyMessageText = async (message) => {
      const id = message?.id;
      const value = (message?.text ?? "").toString();
      if (!id || !value.trim()) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          // Fallback for older browsers / non-secure contexts
          const ta = document.createElement("textarea");
          ta.value = value;
          ta.setAttribute("readonly", "");
          ta.style.position = "absolute";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }

        // show check icon
        copiedMap.value = { ...copiedMap.value, [id]: true };

        // reset timer if user clicks again
        if (copiedTimers.has(id)) {
          clearTimeout(copiedTimers.get(id));
        }

        const t = setTimeout(() => {
          const next = { ...copiedMap.value };
          delete next[id];
          copiedMap.value = next;
          copiedTimers.delete(id);
        }, 1200);

        copiedTimers.set(id, t);
      } catch (e) {
        console.error("Failed to copy:", e);
      }
    };

    const getAudioBacklogMs = () => {
      const ctx = audioCtx.value;
      if (!ctx) return 0;
      const backlogSec = (audioNextTime.value || 0) - ctx.currentTime;
      return Math.max(0, backlogSec * 1000);
    };

    // Schedules a UI update for a given message_id, pacing it relative to audio backlog
    const scheduleTextRender = (mid, nextText) => {
      textPending.set(mid, nextText);

      // keep per-message ordering even if deltas arrive fast
      const prev = textChain.get(mid) || Promise.resolve();
      const p = prev.then(
        () =>
          new Promise((resolve) => {
            // base throttle + extra delay if audio is behind
            const backlog = getAudioBacklogMs();
            const extra = Math.max(0, backlog - TEXT_AUDIO_LEAD_MS);
            const delay = Math.min(
              TEXT_MAX_DELAY_MS,
              Math.max(TEXT_FLUSH_MS, extra),
            );

            // if a timer already exists, don't stack them, just let the latest pending text win
            if (textFlushTimers.has(mid)) return resolve();

            const t = setTimeout(() => {
              textFlushTimers.delete(mid);
              const latest = textPending.get(mid);
              if (latest != null) {
                console.debug("[TEXT-RENDER] t=", performance.now().toFixed(2), "len=", latest.length);
                session.updateCurrentChatMessageText(mid, latest);
                queueScroll();
              }
              resolve();
            }, delay);

            textFlushTimers.set(mid, t);
          }),
      );

      textChain.set(mid, p);
    };

    // Drip-feeds buffered delta tokens at a fixed pace regardless of display refresh rate.
    // Uses a time budget so 120Hz and 60Hz screens see the same token speed.
    const startTextDrain = (mid) => {
      if (textDrainRafs.has(mid)) return;
      textDrainLastTime.set(mid, performance.now());

      const tick = (timestamp) => {
        const queue = textTokenQueues.get(mid);
        const s = assistantStream.get(mid);
        if (!queue || !s || queue.length === 0) {
          textDrainRafs.delete(mid);
          textDrainLastTime.delete(mid);
          return;
        }
        const last = textDrainLastTime.get(mid) ?? timestamp;
        const n = Math.min(Math.max(1, Math.floor((timestamp - last) / DRAIN_MS_PER_TOKEN)), queue.length);
        let added = "";
        for (let i = 0; i < n; i++) added += queue.shift();
        textDrainLastTime.set(mid, last + n * DRAIN_MS_PER_TOKEN);
        s.displayedText = (s.displayedText || "") + added;
        session.updateCurrentChatMessageText(s.assistantId, s.displayedText);
        queueScroll();
        textDrainRafs.set(mid, requestAnimationFrame(tick));
      };

      textDrainRafs.set(mid, requestAnimationFrame(tick));
    };

    // Cancels any running drain and immediately shows the full accumulated text.
    const flushTextDrain = (mid) => {
      const rafId = textDrainRafs.get(mid);
      if (rafId) { cancelAnimationFrame(rafId); textDrainRafs.delete(mid); }
      textDrainLastTime.delete(mid);
      const queue = textTokenQueues.get(mid);
      const s = assistantStream.get(mid);
      if (s && queue && queue.length > 0) {
        s.displayedText = s.textAcc;
        session.updateCurrentChatMessageText(s.assistantId, s.textAcc);
        queueScroll();
      }
      textTokenQueues.delete(mid);
    };

    // Initialize Web Speech API
    if (typeof window !== "undefined") {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (SpeechRecognition) {
        speechSupported.value = true;
        recognizer = new SpeechRecognition();
        recognizer.lang = "en-US";
        recognizer.continuous = true;
        // On Android Chrome, interim results are incorrectly marked as isFinal,
        // causing duplicate accumulation. Disable on Android; not used for live display.
        const isAndroid = /android/i.test(navigator.userAgent);
        recognizer.interimResults = !isAndroid;

        // For development
        recognizer.onaudiostart = () => console.log("SR: audio start");
        recognizer.onaudioend = () => console.log("SR: audio end");
        recognizer.onsoundstart = () => console.log("SR: sound start");
        recognizer.onsoundend = () => console.log("SR: sound end");
        recognizer.onspeechstart = () => console.log("SR: speech start");
        recognizer.onspeechend = () => console.log("SR: speech end");
        recognizer.onnomatch = (e) => console.log("SR: no match", e);

        recognizer.onstart = () => {
          recording.value = true;
          console.log("Voice recording started");
        };

        recognizer.onend = async () => {
          console.log("Voice recording ended (browser ended SR)");

          // If still holding, we treat this as an internal SR segmentation, NOT a stop
          if (voiceMode.value && isHoldingPTT.value && !waiting.value) {
            // Keep UI as "Recording..." (prevents visible pause)
            recording.value = true;

            // Restart quickly — reset state for new SR session
            interimTranscriptBuf = "";
            setTimeout(() => {
              lastProcessedIndex = -1;
              try {
                recognizer.start();
              } catch (e) {
                console.warn("restart failed", e);
              }
            }, 10);

            return;
          }

          // Real end (user released / or voice mode off)
          recording.value = false;

          // Append any words SR hadn't finalized yet before stop() was called
          const transcript = (
            finalTranscriptBuf.value +
            " " +
            interimTranscriptBuf
          ).trim();
          finalTranscriptBuf.value = "";
          interimTranscriptBuf = "";

          if (transcript) {
            console.log("Transcribed text (final):", transcript);
            const turnId = currentVoiceTurnId.value;
            currentVoiceTurnId.value = null; // clear now that turn ended
            await sendVoiceMessage(transcript, turnId);
          }
        };

        recognizer.onerror = (event) => {
          console.error("Speech recognition error:", event.error);

          // While holding: treat transient errors like "no-speech" as restartable
          if (
            voiceMode.value &&
            isHoldingPTT.value &&
            !waiting.value &&
            (event.error === "no-speech" || event.error === "aborted")
          ) {
            recording.value = true;
            setTimeout(() => {
              try {
                recognizer.start();
              } catch (e) {
                console.warn("restart failed", e);
              }
            }, 120);
            return;
          }

          recording.value = false;
        };

        recognizer.onresult = (event) => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal && i > lastProcessedIndex) {
              lastProcessedIndex = i;
              finalTranscriptBuf.value += (result[0].transcript || "") + " ";
              interimTranscriptBuf = ""; // cleared once SR finalizes it
            } else if (!result.isFinal) {
              interimTranscriptBuf = result[0].transcript || "";
            }
          }
        };
      } else {
        console.warn("Web Speech API not supported in this browser");
      }
    }

    const handlePTTDown = async (e) => {
      if (isAudioBusy.value || isAnyAudioLoading.value) return;

      isHoldingPTT.value = true;
      if (waiting.value || !speechSupported.value) return;
      if (recording.value) return;

      pointerId.value = e.pointerId;
      e.currentTarget?.setPointerCapture?.(e.pointerId);

      // Create turn id + start recording user audio
      currentVoiceTurnId.value =
        (crypto?.randomUUID && crypto.randomUUID()) || `t_${Date.now()}`;

      // On Android, MediaRecorder and SpeechRecognition cannot share the mic.
      // Skip user audio recording on Android so speech recognition can function.
      const isAndroid = /android/i.test(navigator.userAgent);
      if (!isAndroid) {
        await startUserAudioRecording();
        if (!isHoldingPTT.value) return; // permission dialog interrupted; user no longer holding
      }

      // Interrupt Olivia audio immediately
      suppressAssistantAudio.value = true;
      stopAssistantAudio();

      await ensureAudio();
      if (!isHoldingPTT.value) return; // user released during ensureAudio; do not start recording
      startRecordingSafe();
    };

    // global listeners so release works even if pointer leaves button/window
    const handlePTTUp = (e) => {
      if (!isHoldingPTT.value) return;
      if (
        pointerId.value != null &&
        e?.pointerId != null &&
        e.pointerId !== pointerId.value
      )
        return;
      stopRecordingSafe();
    };
    const handlePTTCancel = () => stopRecordingSafe();

    const handleBeforeUnload = () => {
      // voiceMode might be true or false; just teardown to ensure socket closes
      teardownVoiceMode("beforeunload");
    };

    onMounted(() => {
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("pointerup", handlePTTUp);
      window.addEventListener("pointercancel", handlePTTCancel);
      window.addEventListener("blur", handlePTTCancel); // if user alt-tabs mid-hold
      if (session.rolesIncludes("admin")) {
        console.log("Voice mode for admin users");
        isAdminView.value = true;
      }
      const group = session.groupName?.toLowerCase() ?? "";
      if (group.startsWith("military")) {
        isMilitaryGroup.value = true;
      }
      const ua = navigator.userAgent;
      isVoiceCompatibleBrowser.value =
        /SamsungBrowser/.test(ua) ||
        (/Safari\//.test(ua) &&
          !/Chrome\//.test(ua) &&
          !/OPT\//.test(ua) &&
          !/OPR\//.test(ua)) ||
        (/Chrome\//.test(ua) &&
          !/Edg\//.test(ua) &&
          !/SamsungBrowser/.test(ua) &&
          !/OPR\//.test(ua));

      mql = window.matchMedia("(min-width: 769px)");
      updateIsDesktop();

      // keep it updated when resizing / rotating devices
      if (mql.addEventListener) {
        mql.addEventListener("change", updateIsDesktop);
      } else {
        // Safari fallback
        mql.addListener(updateIsDesktop);
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      teardownVoiceMode("unmount");
      window.removeEventListener("pointerup", handlePTTUp);
      window.removeEventListener("pointercancel", handlePTTCancel);
      window.removeEventListener("blur", handlePTTCancel);
      // clear timers for copying
      for (const t of copiedTimers.values()) clearTimeout(t);
      copiedTimers.clear();
      // clear playing audio
      stopPlayback();
      for (const url of audioUrlByChatId.values()) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          console.warn("Failed to clear playing audio");
        }
      }
      audioUrlByChatId.clear();

      if (mql) {
        if (mql.removeEventListener) {
          mql.removeEventListener("change", updateIsDesktop);
        } else {
          mql.removeListener(updateIsDesktop);
        }
        mql = null;
      }
    });

    const startRecordingSafe = async () => {
      if (!recognizer || !speechSupported.value || waiting.value) return;
      if (recording.value || isStarting.value) return;

      isStarting.value = true;
      lastProcessedIndex = -1;
      try {
        recognizer.start();
      } catch (e) {
        console.error("startRecordingSafe failed:", e);
        recording.value = false;
      } finally {
        // let onstart flip recording=true; we just clear the starting lock
        setTimeout(() => (isStarting.value = false), 150);
      }
    };

    const stopRecordingSafe = () => {
      isHoldingPTT.value = false;
      if (!recognizer) return;

      // Stop user audio recorder
      stopUserAudioRecording();

      // Allow Olivia audio again for future turns
      suppressAssistantAudio.value = false;

      try {
        // stop triggers onresult (if any) then onend
        recognizer.stop();
      } catch (e) {
        console.error("stopRecordingSafe failed:", e);
        recording.value = false;
      }
    };

    const stopAssistantAudio = () => {
      try {
        // 1) Stop all currently scheduled sources
        for (const src of activeAudioSources) {
          try {
            src.stop(0);
          } catch {
            console.warn("audio is not stopped");
          }
        }
        activeAudioSources.clear();

        // 2) Reset scheduler cursor so backlog is cleared
        const ctx = audioCtx.value;
        if (ctx) {
          audioNextTime.value = ctx.currentTime;
        }

        // 3) Clear any odd-byte remainder buffer
        pcmRemainder = new Uint8Array(0);
      } catch (e) {
        console.warn("stopAssistantAudio failed:", e);
      }
    };

    const queueScroll = () => {
      if (scrollQueued) return;
      scrollQueued = true;

      requestAnimationFrame(async () => {
        scrollQueued = false;
        await nextTick();
        scrollToBottom();
      });
    };

    // A turn failed on the backend. Shared by the text and voice sockets, which
    // receive the same frame.
    //
    // Two things have to happen before anything is said. The turn's empty
    // assistant bubble was created by assistant_started before the model was
    // ever called, so it has to be withdrawn: an infrastructure failure written
    // into Olivia's bubble reads as something the coach said, which is
    // confusing mid-session and would sit in the transcript as coaching
    // content. And `waiting` has to clear, or the composer stays locked.
    const maintenance = computed(() => session.maintenance);

    const maintenanceBlocked = computed(
      () => maintenanceBlockedLive.value || Boolean(maintenance.value?.turns_blocked)
    );

    const maintenanceNewSessionBlocked = computed(() =>
      Boolean(maintenance.value?.new_session_blocked)
    );

    // False only in the gap between this participant's own period closing
    // (newSessionNeeded flips) and the global window actually opening. True
    // during the window (composer/banners hidden, maintenance-pending card
    // shown by Case 2) AND after it releases (composer/banners still hidden,
    // but Case 2 now shows "Begin a new session!" since
    // maintenanceNewSessionBlocked has gone false) -- maintenanceBlocked
    // alone can't tell those last two apart, since it reads false in both the
    // gap and after release. See the matching heldByMaintenance in
    // stores/session.js for the full reasoning; this stays in sync with it so
    // currentChatHistory and the composer never disagree about which state
    // they're in.
    const heldByMaintenance = computed(
      () =>
        newSessionNeeded.value &&
        (maintenanceBlocked.value || !maintenanceNewSessionBlocked.value)
    );

    // The server's clock, not the browser's. Capturing the offset once means a
    // participant whose machine is minutes out still sees the right time.
    const serverSkewMs = computed(() => {
      const serverNow = maintenance.value?.server_now;
      if (!serverNow) return 0;
      return new Date(serverNow).getTime() - Date.now();
    });

    // maintenanceClosingSoon reads Date.now() directly, which Vue cannot
    // track as a dependency -- left alone, this computed only re-evaluates
    // when `maintenance` itself changes, i.e. on the next fetch (mount, tab
    // refocus, or an explicit recheck). A participant who has the tab open
    // and untouched from before the notice window opens would then never see
    // it: the clock crosses the threshold with nothing to prompt Vue to look
    // again. clockTick exists purely to be that prompt -- its value is never
    // read for anything, only used as a dependency so bumping it forces a
    // re-run of this computed against the SAME turns_close_at, at the one
    // moment the answer changes.
    //
    // Deliberately not a repeating poll: the data (turns_close_at) does not
    // go stale on its own, so there is nothing to fetch again. One scheduled
    // callback per fetch, timed for the exact instant it's needed, costs
    // nothing and touches the network not at all -- see the watch() below.
    const clockTick = ref(0);
    let noticeTimer = null;

    const maintenanceClosingSoon = computed(() => {
      clockTick.value; // eslint-disable-line no-unused-expressions
      const closeAt = maintenance.value?.turns_close_at;
      if (!closeAt || maintenanceBlocked.value) return false;
      const minutesAway =
        (new Date(closeAt).getTime() - (Date.now() + serverSkewMs.value)) / 60000;
      return minutesAway > 0 && minutesAway <= MAINTENANCE_NOTICE_MINUTES;
    });

    // Reschedules whenever a fresh turns_close_at arrives. If the notice
    // threshold is already in the past (closes within the hour right now),
    // maintenanceClosingSoon already reads true from this same fetch, so
    // there is nothing to schedule -- the timer is only for the future case.
    watch(
      () => maintenance.value?.turns_close_at,
      (closeAt) => {
        if (noticeTimer) {
          clearTimeout(noticeTimer);
          noticeTimer = null;
        }
        if (!closeAt) return;
        const msUntilNotice =
          new Date(closeAt).getTime() -
          serverSkewMs.value -
          MAINTENANCE_NOTICE_MINUTES * 60000 -
          Date.now();
        if (msUntilNotice > 0) {
          noticeTimer = setTimeout(() => {
            noticeTimer = null;
            clockTick.value++;
          }, msUntilNotice);
        }
      }
    );

    // Formatted in the participant's own locale and zone straight from the
    // instant, so a military participant reads 2:00 AM and a civilian 1:00 AM
    // without the client knowing anything about either zone.
    const maintenanceCloseTimeLabel = computed(() => {
      const closeAt = maintenance.value?.turns_close_at;
      if (!closeAt) return "";
      return new Date(closeAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    });

    // Whether anything is rendered directly above the composer. Both banners
    // share that slot and are mutually compatible, so the composer only needs
    // to know that one of them is there, not which.
    //
    // The other v-if conditions those banners carry are the same ones the
    // composer itself carries, so repeating them here would be noise.
    const bannerAbove = computed(
      () => serverDown.value || maintenanceBlocked.value || maintenanceClosingSoon.value
    );

    const maintenanceMessage = computed(() => {
      if (maintenanceBlocked.value) {
        return "Nightly maintenance is in progress. Your session will be available again shortly.";
      }
      return `This session closes at ${maintenanceCloseTimeLabel.value} for nightly maintenance. Please wrap up.`;
    });

    // Release happens when the batch finishes, which is not a time anyone can
    // predict, so the page cannot compute it. Rather than poll, it re-asks on
    // an explicit human action and on tab focus -- both things the participant
    // was going to do anyway, and neither generating background traffic.
    const recheckMaintenance = async () => {
      if (maintenanceChecking.value) return;
      maintenanceChecking.value = true;
      try {
        await session.fetchChatHistory();
        // Without this, newSessionNeeded stays at whatever it was on last
        // mount/sync -- fine while it hasn't changed, wrong the moment a
        // participant's own period closes while the tab is already open and
        // they click Check again (or switch back to it, which calls this via
        // onVisibilityChange). heldByMaintenance then mixes a stale local
        // flag with live computeds and the page falls through to the wrong
        // Case in the v-if/else-if chain -- an empty "type a message" prompt
        // behind a permanently-disabled composer, with no explanation.
        newSessionNeeded.value = session.newSessionNeeded;
        if (!session.maintenance?.turns_blocked) {
          maintenanceBlockedLive.value = false;
        }
      } finally {
        maintenanceChecking.value = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Not gated on "are we already known to be blocked" -- both flags in
      // that check are computed from the same stale store data a returning
      // tab exists to correct. A participant whose tab went stale while
      // maintenance armed (composer/banner still showing normally, because
      // nothing has told this tab otherwise) switches away and back, the old
      // guard reads the stale "nothing's wrong" state, and does nothing --
      // the one passive recovery path silently no-ops in exactly the case it
      // exists for. Still purely event-driven, not a poll: this only ever
      // fires on a real visibility change, at whatever rate the participant
      // actually switches tabs.
      recheckMaintenance();
    };

    const handleWsError = (evt) => {
      const code = evt?.code || "llm_error";
      const mid = evt?.message_id;
      const text =
        evt?.message || "Something went wrong. Please try sending that again.";

      // Maintenance is not a failed turn. The server refused the message before
      // anything ran, so nothing was recorded and it must not leave a System
      // bubble: that treatment means "this turn failed", and this one never
      // started. Both optimistic bubbles are withdrawn -- the user's, with its
      // text going back into the composer so the participant can send it again
      // after the window rather than retyping it, and the assistant's empty
      // placeholder (added by sendTextMessageViaWs before the reply ever
      // starts streaming), which nothing will ever fill in since this turn
      // never ran. Missing that second removal left a permanently blank
      // "Olivia" bubble stuck in the transcript -- confirmed live, not
      // hypothetical.
      if (code === "maintenance_turns") {
        assistantStream.delete(mid);
        textTokenQueues.delete(mid);
        const unsent = session.removePendingUserMessage(mid);
        if (unsent && !textInput.value) textInput.value = unsent;
        session.removeCurrentChatMessage(mid);
        waiting.value = false;
        // Immediate and synchronous: hides the composer/banners this instant,
        // without waiting on the network. On its own this isn't enough --
        // heldByMaintenance hides the composer, but currentChatHistory still
        // holds the stale (pre-window) session, and Case 1's own v-if only
        // checks displayedChatHistory.length, not heldByMaintenance. Left
        // alone that's a dead end: chat history rendering with the composer
        // and both banners hidden underneath it, no explanation, confirmed
        // live. The rejected turn is itself proof the window is genuinely
        // open, so rather than tell the participant to refresh, use that as
        // the trigger recheckMaintenance() already exists to respond to --
        // same fetch "Check again" makes, just fired from one more place.
        // Fire-and-forget: nothing here needs to block on it resolving.
        maintenanceBlockedLive.value = true;
        recheckMaintenance();
        return;
      }

      if (mid) {
        assistantStream.delete(mid);
        textTokenQueues.delete(mid);
        if (evt?.transcript_text) {
          // The backend records this turn, so the bubble stays and becomes a
          // System bubble. The row comes back on the next history fetch either
          // way, so removing it here would only make the live view disagree
          // with the view after a refresh.
          //
          // The stored marker is kept on the entry for parity with what the
          // server holds; displayText() is what the participant actually reads.
          session.upsertCurrentChatMessage({
            id: mid,
            text: evt.transcript_text,
            errorCode: code,
          });
        } else {
          session.removeCurrentChatMessage(mid);
        }
      }
      waiting.value = false;
      console.error(`WS error event (${code}):`, evt);

      if (code === "llm_unavailable") {
        // No popup. The turn already leaves a System bubble in the transcript
        // and raises the banner, and a modal on top of both would be a third
        // surface saying one thing to someone who is looking at the bottom of
        // the conversation anyway.
        serverDown.value = true;
        return;
      }

      // No row was written for this one, so there is no bubble to carry it and
      // the popup is the only signal left.
      swal.fire({ icon: "error", title: "Message not delivered", text });
    };

    // Both rows of a failed turn carry error_code, because analysis wants the
    // turn as one unit. Only the assistant half becomes a notice: the
    // participant really did send their message, so it stays their message,
    // with their name and avatar. Marking it too would rewrite what they said
    // as a system announcement, which is worse than the bug it came from.
    const isSystemNotice = (message) =>
      Boolean(message?.errorCode) && Boolean(message?.isResponse);

    // What a failed turn says in the transcript.
    //
    // Derived from the code rather than read from the row, because the stored
    // text is `[Something went wrong. Please try it again.]`, a marker kept for
    // analysis. Deriving is also what makes the live view and the view after a
    // refresh identical: both have only the code to work from.
    //
    // Both messages spell out that resending is required, not just possible:
    // the backend now drops a failed turn from what the coach model and the
    // session summary are shown, so unless the participant sends it again,
    // it is as if they never said it -- "try again" alone reads as optional,
    // and here it is not.
    const displayText = (message) => {
      if (!isSystemNotice(message)) return message.text;
      const name = session.chatbotName;
      if (message.errorCode === "llm_unavailable") {
        return (
          `${name} could not reply because the coaching service was ` +
          "temporarily unavailable, so your message was not delivered. " +
          `${name} will not see it unless you send it again -- please resend ` +
          "in a few minutes."
        );
      }
      return (
        `${name} could not reply to that message, so it was not delivered. ` +
        `${name} will not see it unless you send it again -- please resend it.`
      );
    };

    // Any turn that reaches the model proves the server is back.
    const clearServerDown = () => {
      serverDown.value = false;
      serverDownNotified.value = false;
    };

    const ensureRealtime = () => {
      if (realtime) return realtime;

      // So a captured WS error/close is filterable back to the participant
      // and session it happened in, rather than an anonymous stack trace.
      Sentry.setUser({ username: session.username });
      Sentry.setTag("session_id", session.currentSessionId);

      realtime = new RealtimeChatClient({
        apiBaseUrl: apiClient.defaults.baseURL,
      });

      realtime.onOpen = () => {
        console.log("Realtime WS opened");
        if (session.rolesIncludes("admin") && session.debugEnabled) {
          debugApi.retrieveDebugLogs(session.currentSessionId, [], null)
            .then(logs => session.setDebugLogs(logs))
            .catch(e => console.error("debug log fetch on realtime open failed:", e));
        }
      };
      realtime.onError = (e) => console.error("Realtime WS error", e);

      // Handle server events -> append bot message (adjust types to match backend)
      realtime.onEvent = async (evt) => {
        try {
          const type = evt?.type;

          // Force-end must be handled before the generic session_ended branch
          // below: it keeps the socket open for the follow-up terminal event,
          // where a normal end closes it.
          if (type === "session_ended" && forceEnding.value) {
            waiting.value = false;
            const hist = session.currentChatHistory;
            if (hist?.length) hist[hist.length - 1].end = true;
            voiceMode.value = false;
            // Keep the socket open; the backend follows up with
            // new_session_available or summary_pending.
            return;
          }
          if (type === "new_session_available" || type === "summary_pending") {
            realtime?.close();
            realtime = null;
            await finishForceEnd(type);
            return;
          }

          const mid = evt?.message_id;

          if (!type) return;
          if (PER_MESSAGE_WS_EVENTS.has(type) && !mid) return;
          // Recovery. Not assistant_started, which is sent before the model
          // is called and so fires even while the server is down. Anything
          // after it means a turn actually reached the model.
          if (
            serverDown.value &&
            type !== "assistant_started" &&
            type.startsWith("assistant_")
          ) {
            clearServerDown();
          }

          // 1) When assistant starts, create a placeholder assistant message
          if (type === "assistant_started") {
            // placeholder already exists from sendVoiceMessage()
            if (!assistantStream.has(mid)) {
              // fallback safety in case an event arrives without our local send (rare)
              const assistantId = mid;
              session.addCurrentChatHistoryEntry({
                id: assistantId,
                isResponse: true,
                timestamp: new Date().toISOString(),
                sender: session.chatbotName,
                text: "",
              });
              assistantStream.set(mid, { assistantId, textAcc: "" });
            }
            queueScroll();
            return;
          }

          // 2) Text deltas (some models emit output_text.* OR transcript.*)
          if (
            type === "assistant_text_delta" ||
            type === "assistant_audio_transcript_delta"
          ) {
            const s = assistantStream.get(mid);
            if (!s) return;

            s.textAcc += evt.delta || "";
            scheduleTextRender(mid, s.textAcc);
            return;
          }

          // 3) Text done
          if (
            type === "assistant_text_done" ||
            type === "assistant_audio_transcript_done"
          ) {
            const s = assistantStream.get(mid);
            if (!s) return;

            const full = (evt.full_text || "").trim();
            if (full) {
              s.textAcc = full;

              // cancel any pending timer and render immediately
              const t = textFlushTimers.get(mid);
              if (t) {
                clearTimeout(t);
                textFlushTimers.delete(mid);
              }
              textPending.set(mid, full);
              session.updateCurrentChatMessageText(s.assistantId, full);
            }
            queueScroll();
            return;
          }

          // 4) Audio deltas (base64 pcMU)
          if (type === "assistant_audio_delta") {
            setAssistantSpeaking(true);
            const b64 = evt.audio_base64;
            if (!b64) return;

            const chunk = base64ToUint8(b64);

            // stitch any leftover odd byte from last chunk
            let bytes = chunk;
            if (pcmRemainder.length) {
              bytes = concatUint8(pcmRemainder, chunk);
              pcmRemainder = new Uint8Array(0);
            }

            // if odd length, save last byte for next chunk
            if (bytes.length % 2 === 1) {
              pcmRemainder = bytes.slice(bytes.length - 1);
              bytes = bytes.slice(0, bytes.length - 1);
            }

            if (bytes.length === 0) return;

            const f32 = decodePcm16leToFloat32(bytes);
            await playFloat32Chunk(f32);
            return;
          }

          // 5) Audio done (optional hook)
          if (type === "assistant_audio_done") {
            return;
          }

          // 6) Turn done
          if (type === "assistant_done") {
            waiting.value = false;
            setAssistantSpeaking(true);
            return;
          }

          // 7) Persistent data (chat_id + debug logs)
          if (type === "assistant_persisted") {
            if (evt.chat_id != null) {
              attachChatIdToMessage(mid, evt.chat_id);
            }
            if (session.rolesIncludes("admin") && session.debugEnabled) {
              if (evt.debug_ids?.length) {
                debugApi.retrieveDebugLogs(session.currentSessionId, evt.debug_ids, null)
                  .then(newLogs => session.addDebugLogs(newLogs))
                  .catch(e => console.error("realtime debug log fetch failed:", e));
              }
              const _sid = session.currentSessionId;
              setTimeout(() => {
                debugApi.retrieveDebugLogs(_sid, [], null)
                  .then(logs => session.setDebugLogs(logs))
                  .catch(e => console.error("realtime delayed debug log fetch failed:", e));
              }, 5000);
            }
            return;
          }

          // 7b) Session ended
          if (type === "session_ended") {
            waiting.value = false;
            // full_text is the persisted message and is the only delivery of the
            // post-survey link: the core stream finished before it was appended.
            const s = mid ? assistantStream.get(mid) : null;
            const full = (evt.full_text || "").trim();
            if (s && full) {
              const t = textFlushTimers.get(mid);
              if (t) {
                clearTimeout(t);
                textFlushTimers.delete(mid);
              }
              s.textAcc = full;
              textPending.set(mid, full);
              session.updateCurrentChatMessageText(s.assistantId, full);
              queueScroll();
            }
            const hist = session.currentChatHistory;
            if (hist?.length) hist[hist.length - 1].end = true;
            voiceMode.value = false;
            realtime?.close();
            realtime = null;
            return;
          }

          // 8) User persistent data
          if (type === "user_persisted") {
            const turnId = evt.message_id;
            const chatId = evt.chat_id;
            if (!turnId || !chatId) return;

            // Attach chat_id to the user bubble so playback can work
            const lists = [
              session.currentChatHistory,
              session.selectedChatHistory,
            ];
            for (const arr of lists) {
              const m = arr?.find?.((x) => x?.voice_turn_id === turnId);
              if (m) {
                m.chat_id = chatId;
                break;
              }
            }

            const pending = pendingUserAudioByTurnId.get(turnId);
            if (!pending?.blob) return;

            try {
              await chatApi.uploadUserAudio({
                session_id: session.currentSessionId,
                chat_id: chatId,
                blob: pending.blob,
                contentType: pending.contentType,
              });
              pendingUserAudioByTurnId.delete(turnId);
            } catch (e) {
              console.error("User audio upload failed:", e);
              // keep it in the map so you can retry later if you want
            }
            return;
          }

          // 9) Error
          if (type === "error") {
            handleWsError(evt);
            return;
          }
        } catch (e) {
          console.error("Realtime onEvent handler failed:", e);
          waiting.value = false;
        }
      };

      realtime.onClose = (ev) => {
        console.log("Realtime WS closed", ev?.code, ev?.reason);
        // Safety net: a socket that closes mid-turn (force-end included) would
        // otherwise leave the input locked behind `waiting` with no further
        // event to clear it.
        if (forceEnding.value) forceEnding.value = false;
        waiting.value = false;
        if (!voiceMode.value) return;

        // This reconnect is what makes a dropped connection invisible to the
        // participant -- worth a breadcrumb even on top of the service's own
        // abnormal-close capture, so a Sentry timeline shows whether it was a
        // one-off blip or a loop (this has no retry cap or backoff).
        Sentry.addBreadcrumb({
          category: "ws.realtime",
          message: "Auto-reconnecting after close",
          level: "info",
          data: { session_id: session.currentSessionId, code: ev?.code },
        });

        setTimeout(() => {
          if (!voiceMode.value) return;
          if (!realtime) return; // <-- key guard
          realtime.connect({ sessionId: session.currentSessionId });
        }, 250);
      };

      return realtime;
    };

    const ensureRealtimeConnected = () => {
      const rt = ensureRealtime();
      if (!rt.connected) {
        rt.connect({ sessionId: session.currentSessionId });
      }
      return rt;
    };

    const ensureTextChat = () => {
      if (textChat) return textChat;

      // So a captured WS error/close is filterable back to the participant
      // and session it happened in, rather than an anonymous stack trace.
      Sentry.setUser({ username: session.username });
      Sentry.setTag("session_id", session.currentSessionId);

      textChat = new TextChatClient({
        apiBaseUrl: apiClient.defaults.baseURL,
      });

      textChat.onOpen = () => {
        if (session.rolesIncludes("admin") && session.debugEnabled) {
          debugApi.retrieveDebugLogs(session.currentSessionId, [], null)
            .then(logs => session.setDebugLogs(logs))
            .catch(e => console.error("debug log fetch on open failed:", e));
        }
      };
      textChat.onError = (e) => console.error("TextChat WS error", e);

      textChat.onEvent = async (evt) => {
        try {
          const type = evt?.type;

          // Force-end must be handled before the generic session_ended branch
          // below: it keeps the socket open for the follow-up terminal event,
          // where a normal end closes it.
          if (type === "session_ended" && forceEnding.value) {
            waiting.value = false;
            const hist = session.currentChatHistory;
            if (hist?.length) hist[hist.length - 1].end = true;
            // Keep the socket open; the backend follows up with
            // new_session_available or summary_pending.
            return;
          }
          if (type === "new_session_available" || type === "summary_pending") {
            textChat?.close();
            textChat = null;
            await finishForceEnd(type);
            return;
          }

          const mid = evt?.message_id;

          if (!type) return;
          if (PER_MESSAGE_WS_EVENTS.has(type) && !mid) return;
          // Recovery. Not assistant_started, which is sent before the model
          // is called and so fires even while the server is down. Anything
          // after it means a turn actually reached the model.
          if (
            serverDown.value &&
            type !== "assistant_started" &&
            type.startsWith("assistant_")
          ) {
            clearServerDown();
          }

          if (type === "assistant_started") {
            if (!assistantStream.has(mid)) {
              session.addCurrentChatHistoryEntry({
                id: mid,
                isResponse: true,
                timestamp: new Date().toISOString(),
                sender: session.chatbotName,
                text: "",
              });
              assistantStream.set(mid, { assistantId: mid, textAcc: "", displayedText: "" });
            }
            queueScroll();
            return;
          }

          if (type === "assistant_text_delta") {
            const s = assistantStream.get(mid);
            if (!s) return;
            s.textAcc += evt.delta || "";
            if (!textTokenQueues.has(mid)) textTokenQueues.set(mid, []);
            textTokenQueues.get(mid).push(evt.delta || "");
            startTextDrain(mid);
            return;
          }

          if (type === "assistant_text_done") {
            const s = assistantStream.get(mid);
            if (!s) return;
            const full = (evt.full_text || "").trim();
            // Update the canonical full text; drain will display it progressively.
            // If the drain already finished (fast response), sync displayedText now.
            if (full) {
              s.textAcc = full;
              if (!textDrainRafs.has(mid) && !textTokenQueues.get(mid)?.length) {
                s.displayedText = full;
                session.updateCurrentChatMessageText(s.assistantId, full);
              }
            }
            return;
          }

          if (type === "assistant_done") {
            // Let the drain complete naturally so the user sees the last tokens appear.
            // Flush only if the queue is unexpectedly large (>50 tokens still pending).
            const q = textTokenQueues.get(mid);
            if (q && q.length > 50) flushTextDrain(mid);
            waiting.value = false;
            return;
          }

          if (type === "assistant_persisted") {
            if (evt.chat_id != null) {
              attachChatIdToMessage(mid, evt.chat_id);
            }
            if (session.rolesIncludes("admin") && session.debugEnabled) {
              if (evt.debug_ids?.length) {
                debugApi.retrieveDebugLogs(session.currentSessionId, evt.debug_ids, null)
                  .then(newLogs => session.addDebugLogs(newLogs))
                  .catch(e => console.error("debug log fetch failed:", e));
              }
              // Delayed fetch picks up async Celery evaluation logs (~5s after turn completes)
              const _sid = session.currentSessionId;
              setTimeout(() => {
                debugApi.retrieveDebugLogs(_sid, [], null)
                  .then(logs => session.setDebugLogs(logs))
                  .catch(e => console.error("delayed debug log fetch failed:", e));
              }, 5000);
            }
            return;
          }

          if (type === "user_persisted") {
            const chatId = evt.chat_id;
            if (!chatId) return;
            const lists = [
              session.currentChatHistory,
              session.selectedChatHistory,
            ];
            for (const arr of lists) {
              const m = arr?.find?.((x) => x?.ws_message_id === mid);
              if (m) {
                m.chat_id = chatId;
                break;
              }
            }
            return;
          }

          if (type === "session_ended") {
            waiting.value = false;
            // full_text is the persisted message and is the only delivery of the
            // post-survey link: the core stream finished before it was appended.
            const s = mid ? assistantStream.get(mid) : null;
            const full = (evt.full_text || "").trim();
            if (s && full) {
              flushTextDrain(mid);
              s.textAcc = full;
              s.displayedText = full;
              session.updateCurrentChatMessageText(s.assistantId, full);
              queueScroll();
            }
            const hist = session.currentChatHistory;
            if (hist?.length) hist[hist.length - 1].end = true;
            textChat?.close();
            textChat = null;
            return;
          }

          if (type === "error") {
            handleWsError(evt);
            return;
          }
        } catch (e) {
          console.error("TextChat onEvent handler failed:", e);
          waiting.value = false;
        }
      };

      textChat.onClose = (ev) => {
        console.log("TextChat WS closed", ev?.code, ev?.reason);
        // Safety net: a socket that closes mid-turn (force-end included) would
        // otherwise leave the input locked behind `waiting` with no further
        // event to clear it.
        if (forceEnding.value) forceEnding.value = false;
        waiting.value = false;
        textChat = null;
      };

      return textChat;
    };

    const sendTextMessageViaWs = (message, messageId) => {
      const tc = ensureTextChat();
      if (!tc.connected) tc.connect({ sessionId: session.currentSessionId });

      session.addCurrentChatHistoryEntry({
        id: messageId,
        isResponse: true,
        timestamp: new Date().toISOString(),
        sender: session.chatbotName,
        text: "",
      });
      assistantStream.set(messageId, { assistantId: messageId, textAcc: "" });

      tc.sendUserMessage({ content: message, message_id: messageId });
      queueScroll();
    };

    const ensureAudio = async () => {
      if (!audioCtx.value) {
        audioCtx.value = new (window.AudioContext ||
          window.webkitAudioContext)();
        audioNextTime.value = audioCtx.value.currentTime;
      }
      if (audioCtx.value.state === "suspended") {
        try {
          await audioCtx.value.resume();
        } catch {
          console.error("audio is suspended");
        }
      }
    };

    const ensureAudioEl = () => {
      if (audioEl.value) return audioEl.value;

      const el = new Audio();

      // When audio ends naturally → unlock UI
      el.addEventListener("ended", () => {
        const mid = playingMid.value;
        resetPlaybackState(mid);
      });

      // When audio is paused (user pauses or we stop) → unlock UI
      el.addEventListener("pause", () => {
        // If we paused while something was marked playing/loading, clear it
        const mid = playingMid.value;
        if (mid) resetPlaybackState(mid);
      });

      // When audio errors → unlock UI
      el.addEventListener("error", () => {
        const mid = playingMid.value;
        resetPlaybackState(mid);
      });

      audioEl.value = el;
      return el;
    };

    const base64ToUint8 = (b64) => {
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes;
    };

    const concatUint8 = (a, b) => {
      const out = new Uint8Array(a.length + b.length);
      out.set(a, 0);
      out.set(b, a.length);
      return out;
    };

    // PCM16 LE (audio/pcm) -> Float32Array [-1, 1]
    const decodePcm16leToFloat32 = (u8) => {
      // must be even number of bytes (2 bytes per sample)
      const sampleCount = (u8.length / 2) | 0;
      const out = new Float32Array(sampleCount);

      const view = new DataView(u8.buffer, u8.byteOffset, sampleCount * 2);
      for (let i = 0; i < sampleCount; i++) {
        const s16 = view.getInt16(i * 2, true); // little-endian
        out[i] = Math.max(-1, Math.min(1, s16 / 32768));
      }
      return out;
    };

    const playFloat32Chunk = async (f32) => {
      // If user is recording, we interrupt Olivia locally by ignoring audio deltas
      if (suppressAssistantAudio.value) return;

      await ensureAudio();
      const ctx = audioCtx.value;

      const buf = ctx.createBuffer(1, f32.length, audioInputSampleRate);
      buf.copyToChannel(f32, 0);

      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);

      // Track source so we can stop it mid-stream
      activeAudioSources.add(src);
      src.onended = () => activeAudioSources.delete(src);

      const now = ctx.currentTime;
      const startAt = Math.max(now, audioNextTime.value);
      src.start(startAt);
      audioNextTime.value = startAt + buf.duration;
      setAssistantSpeaking(true);
    };

    // Send voice-transcribed message
    const sendVoiceMessage = async (transcript, turnIdOverride = null) => {
      if (!transcript || waiting.value) return;

      if (surveyRequired.value) {
        console.warn("Survey must be acknowledged before sending messages.");
        return;
      }

      waiting.value = true;

      try {
        // 1) Add user's transcribed message to chat immediately
        const now = new Date();
        const userMessageId = Date.now();

        const userMessage = {
          id: userMessageId,
          isResponse: false,
          timestamp: now.toISOString(),
          sender:
            (session.firstname || "").trim() ||
            (session.username || "").trim() ||
            "user",
          text: transcript,
          voice_turn_id: turnIdOverride || currentVoiceTurnId.value || null,
        };

        session.addCurrentChatHistoryEntry(userMessage);
        await nextTick();
        scrollToBottom();

        // 2) If voiceMode is ON -> send via Realtime WS (NEW)
        if (voiceMode.value) {
          const rt = ensureRealtime();
          if (!rt.connected)
            rt.connect({ sessionId: session.currentSessionId });

          // IMPORTANT: message_id must match what backend uses to correlate deltas
          const messageId =
            turnIdOverride ||
            (crypto?.randomUUID && crypto.randomUUID()) ||
            `m_${Date.now()}`;

          // Create assistant placeholder NOW (so there is always a target to stream into)
          const assistantId = messageId;

          session.addCurrentChatHistoryEntry({
            id: messageId,
            isResponse: true,
            timestamp: new Date().toISOString(),
            sender: session.chatbotName,
            text: "",
          });

          assistantStream.set(messageId, { assistantId, textAcc: "" });

          rt.sendUserMessage({
            text: transcript,
            message_id: messageId,
          });

          queueScroll();
          return;
        }

        // voiceMode is off — transcript arrived as a race condition; ignore it
        console.warn("Voice transcript received but voiceMode is off; ignoring.");
        return;
      } catch (error) {
        console.error("Error sending voice message:", error);
        waiting.value = false;
      }
    };

    // const displayedChatHistory = computed(() => {
    //     return session.selectedChatHistory.length ? session.selectedChatHistory : session.currentChatHistory;
    // });
    const displayedChatHistory = computed(() => {
      return session.isViewingCurrentChat
        ? session.currentChatHistory
        : session.selectedChatHistory;
    });

    // "CDT", "EST", ... for the viewer's own zone. Intl rather than date-fns
    // because date-fns's zzz token renders a numeric offset ("GMT-5"), and a
    // participant reading their chat log is far better served by "CDT".
    // Abbreviations track DST on their own, so no seasonal handling is needed.
    const timezoneAbbrev = (d) =>
      new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
        .formatToParts(d)
        .find((p) => p.type === "timeZoneName")?.value ?? "";

    // Timestamps arrive as UTC with an explicit Z -- from the server
    // (crud/chat.py) and from toISOString() locally -- so new Date() parses them
    // correctly and date-fns renders them in the VIEWER's zone, not the group's.
    //
    // The zone is now named, because that difference stops being invisible once
    // military cohorts run on Eastern period boundaries. The same instant reads
    // as "Aug 9, 11:30 PM CDT" to a Chicago admin and "Aug 10, 12:30 AM EDT" to
    // a participant in Annapolis -- a different DAY, which is exactly the hour a
    // period boundary can fall in. Without the abbreviation those two people
    // have no way to tell they are looking at the same message.
    const formatTimestamp = (timestamp) => {
      const d = new Date(timestamp);
      const abbrev = timezoneAbbrev(d);
      return abbrev ? `${format(d, "PPpp")} ${abbrev}` : format(d, "PPpp");
    };

    const scrollToBottom = () => {
      const container =
        historyRef.value ||
        document.querySelector(".chat-history") ||
        document.querySelector(".chat-window");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    };

    const fetchChatHistory = async () => {
      try {
        await session.fetchChatHistory();
      } catch (error) {
        console.error("Error fetching chat history:", error);
      } finally {
        newSessionNeeded.value = session.newSessionNeeded;
        loading.value = false;
      }
    };

    // Demo-only: force-end the current session over the active WebSocket.
    // The backend ends the session, runs the real summarization, and then
    // replies with new_session_available (summaries ready) or summary_pending
    // (timeout / no summary models), handled in the WS onEvent handlers below.
    const forceEndSession = async () => {
      const res = await swal.fire({
        icon: "warning",
        title: "End this session?",
        text: "This closes the current coaching session and prepares a new one.",
        showCancelButton: true,
        confirmButtonText: "End session",
        cancelButtonText: "Cancel",
      });
      if (!res.isConfirmed) return;

      forceEnding.value = true;
      summaryPending.value = false;
      waiting.value = true;

      try {
        if (voiceMode.value) {
          const rt = ensureRealtimeConnected();
          rt.forceEnd();
        } else {
          const tc = ensureTextChat();
          if (!tc.connected) tc.connect({ sessionId: session.currentSessionId });
          tc.forceEnd();
        }
      } catch (e) {
        console.error("Force-end failed:", e);
        forceEnding.value = false;
        waiting.value = false;
        swal.fire({
          icon: "error",
          title: "Could not end the session",
          text: "An unexpected error occurred. Please try again.",
        });
      }
    };

    // Terminal step of the force-end flow, invoked from the WS onEvent handlers
    // once the socket delivers new_session_available or summary_pending.
    const finishForceEnd = async (type) => {
      forceEnding.value = false;
      waiting.value = false;
      if (type === "new_session_available") {
        summaryPending.value = false;
        // Gate is now satisfied; refetch flips newSessionNeeded so the existing
        // "Begin a new session!" button renders.
        await session.fetchChatHistory();
        newSessionNeeded.value = session.newSessionNeeded;
      } else if (type === "summary_pending") {
        // Summaries not ready; show the note + "Start anyway" escape hatch.
        summaryPending.value = true;
      }
      await nextTick();
      scrollToBottom();
    };

    const isChatEnded = computed(() => {
      const lastMessage =
        displayedChatHistory.value[displayedChatHistory.value.length - 1];
      return lastMessage?.end === true;
    });

    const hasProgress = computed(() => {
      return (
        session.isViewingCurrentChat &&
        Array.isArray(session.currentChatHistory) &&
        session.currentChatHistory.length > 1
      );
    });
    // Survey acknowledgment gate
    const surveyAck = ref(false);
    const surveyAckKey = computed(() => {
      // Prefer session id key; fallback to survey URL if no id yet
      if (session.currentSessionId)
        return `surveyAck:${session.currentSessionId}`;
      if (session.surveyLink) return `surveyAck:url:${session.surveyLink}`;
      return "surveyAck:global";
    });
    const surveyRequired = computed(() => {
      // Only require when on current chat + have survey link + not acked
      return (
        session.isViewingCurrentChat &&
        !!session.surveyLink &&
        !surveyAck.value &&
        !hasProgress.value
      );
    });
    const ackSurvey = () => {
      surveyAck.value = true;
      try {
        sessionStorage.setItem(surveyAckKey.value, "1");
      } catch {
        console.error(`Failed to set surveyAckKey.`);
      }
    };

    // Visibility of the header-control card's two rows. Both are only relevant
    // during an active current-chat session.
    const controlsActive = computed(
      () =>
        session.isViewingCurrentChat &&
        !surveyRequired.value &&
        !newSessionNeeded.value &&
        !isChatEnded.value,
    );
    // Voice mode is switched off for the fall study, which does not use it.
    //
    // One constant rather than deleting the eligibility logic below, so turning
    // it back on is a single edit rather than reconstructing who was supposed to
    // see it. Note that logic includes isMilitaryGroup, so without this the
    // spark military cohorts would have been offered voice at launch.
    //
    // This only hides the control. The API refuses voice independently via
    // VOICE_ENABLED, which defaults false -- hiding a button does not stop a
    // direct request, so the two layers are deliberately not wired together.
    const VOICE_MODE_AVAILABLE = false;

    const showVoiceToggle = computed(
      () =>
        VOICE_MODE_AVAILABLE &&
        controlsActive.value &&
        (isDesktop.value || isAdminView.value || isMilitaryGroup.value),
    );
    const showEndControl = computed(
      () => controlsActive.value && session.enableSessionManipulation,
    );

    onMounted(async () => {
      try {
        surveyAck.value = sessionStorage.getItem(surveyAckKey.value) === "1";
      } catch {
        console.log(`No surveyAckKey`);
      }
      await fetchChatHistory();
      // If there's already progress in this chat, implicitly consider the survey "handled"
      if (hasProgress.value && !surveyAck.value) {
        surveyAck.value = true; // so UI unblocks immediately in this tab
        // (optional) also stash to sessionStorage to avoid showing again if the user refreshes
        try {
          sessionStorage.setItem(surveyAckKey.value, "1");
        } catch {
          console.error(`Failed to set surveyAckKey.`);
        }
      }
      // Event-driven rather than polled. A blocked participant coming back to
      // the tab is the moment worth re-asking, and an idle background tab
      // generates nothing. Release is not predictable from the client, since it
      // happens when the batch finishes.
      document.addEventListener("visibilitychange", onVisibilityChange);

      await nextTick();
      scrollToBottom();
    });
    onBeforeUnmount(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (noticeTimer) clearTimeout(noticeTimer);
      if (slowResponseTimer) clearTimeout(slowResponseTimer);
      textChat?.close();
      textChat = null;
      // Clean up speech recognition on component unmount
      if (recognizer && recording.value) {
        try {
          recognizer.stop();
        } catch (error) {
          console.error("Error stopping recognizer on unmount:", error);
        }
      }
    });
    return {
      theme,
      session,
      displayedChatHistory,
      formatTimestamp,
      historyRef,
      scrollToBottom,
      loading,
      waiting,
      waitingLong,
      serverDown,
      maintenanceBlocked,
      maintenanceNewSessionBlocked,
      maintenanceClosingSoon,
      heldByMaintenance,
      bannerAbove,
      maintenanceMessage,
      maintenanceChecking,
      recheckMaintenance,
      isSystemNotice,
      displayText,
      isChatEnded,
      textInput,
      textareaRef,
      newSessionNeeded,
      forceEndSession,
      forceEnding,
      summaryPending,
      showVoiceToggle,
      showEndControl,
      surveyRequired,
      ackSurvey,
      voiceMode,
      recording,
      speechSupported,
      sendVoiceMessage,
      stopRecordingSafe,
      handlePTTDown,
      realtime,
      ensureRealtime,
      ensureRealtimeConnected,
      copiedMap,
      copyMessageText,
      isAdminView,
      isMilitaryGroup,
      isVoiceCompatibleBrowser,
      showBrowserAlert,
      playAudio,
      audioStateByMid,
      isAudioBusy,
      isAnyAudioLoading,
      assistantSpeaking,
      audioInteractionLocked,
      teardownVoiceMode,
      isDesktop,
      sendTextMessageViaWs,
    };
  },
  methods: {
    toggleVoiceMode() {
      // Safety guard: don't allow flipping while audio is active
      if (
        this.isAudioBusy ||
        this.isAnyAudioLoading ||
        this.waiting ||
        this.recording
      ) {
        // revert UI change (since v-model already flipped)
        this.voiceMode = !this.voiceMode;
        return;
      }

      console.log("Voice Mode toggled:", this.voiceMode);

      if (this.voiceMode) {
        // Android Chrome ignores recognizer.lang and uses the device's system language.
        // Warn users if their device language is not English.
        const isAndroid = /android/i.test(navigator.userAgent);
        if (isAndroid && !navigator.language.startsWith("en")) {
          swal.fire({
            icon: "warning",
            text:
              "Voice mode requires your device language to be set to English. " +
              "Please change your Android system language to English in Settings > System > Language, then try again.",
          });
          this.voiceMode = false;
          return;
        }
        this.ensureRealtime().connect({
          sessionId: this.session.currentSessionId,
        });
      } else {
        // full teardown: stop SR/audio + close WS + reset buffers
        this.teardownVoiceMode("toggle_off");
      }
    },

    capitalize(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    async createNewSession() {
      try {
        this.loading = true;
        // Clear any demo force-end state so its banner never lingers over the
        // freshly created session.
        this.summaryPending = false;
        this.forceEnding = false;
        // Instead of creating a new session and loading start text,
        // Just retrieve sessions with new session needed flag and reload them
        await this.session.fetchChatHistory(true);

        if (this.session.newSessionNeeded) {
          // The create attempt didn't go through -- but this branch is also
          // exactly what a correctly-rejected new_session_blocked request
          // looks like: session.js's fetchChatHistory swallows its own
          // errors (logs and returns), so the 503 that routers/session.py
          // raises for a real maintenance block never throws here, it just
          // leaves newSessionNeeded unchanged. Showing "contact an
          // administrator" for that is wrong -- confirmed live, clicking a
          // stale "Begin a new session!" during an armed-but-unrefreshed
          // window produces exactly this dialog, for a participant who did
          // nothing wrong. Recheck real state before deciding which it was.
          await this.recheckMaintenance();
          if (!this.session.newSessionNeeded || this.maintenanceNewSessionBlocked) {
            // Either the recheck resolved it, or it's confirmed maintenance
            // -- Case 2's own pending card already renders the correct
            // message once maintenanceNewSessionBlocked is true, so there is
            // nothing further to say here.
            return;
          }
          swal.fire({
            icon: "error",
            title: "Could not start a new session",
            text: "Your account may not be fully configured. Please contact an administrator.",
          });
          return;
        }

        /*
        await this.session.createNewSession();
        await this.loadStartText();
        this.newSessionNeeded = false;
        await this.$nextTick();
        this.scrollToBottom();
        */
        // No window.open() here. The survey gate above already made the
        // participant open the link and click "Questionnaire completed"
        // before this button was even reachable, so popping the same link
        // again on click was pure duplication -- one browser-tab open that's
        // easy to miss, plus the same link sitting in the gate's own text,
        // which is how the survey was ending up submitted twice.
      } catch (error) {
        console.error("Error creating new session:", error);
        swal.fire({
          icon: "error",
          title: "Could not start a new session",
          text: "An unexpected error occurred. Please try again.",
        });
      } finally {
        this.newSessionNeeded = this.session.newSessionNeeded;
        this.loading = false;
        await this.$nextTick();
        this.scrollToBottom();
      }
    },
    /*
    async loadStartText() {
      try {
        this.loading = true;
        // Call the API method to retrieve the start text
        const startText = await chatApi.getStartText();

        const now = new Date();
        const start = {
          id: Date.now(),
          text: startText,
          isResponse: true,
          timestamp: now.toISOString(),
          sender: this.session.chatbotName,
        };

        this.session.addCurrentChatHistoryEntry(start);
        await this.$nextTick();
        this.scrollToBottom();
      } catch (error) {
        console.error("Error retrieving start text:", error);
      } finally {
        this.loading = false; // Always clear loading state when done
      }
    },
    */
    async sendMessage() {
      if (this.surveyRequired) {
        console.warn("Survey must be acknowledged before sending messages.");
        return;
      }

      const message = this.textInput.trim();
      this.textInput = "";
      if (message === "") return;

      this.waiting = true;
      try {
        // Create and add the user's message to the chat history.
        const now = new Date();
        const messageId = crypto?.randomUUID?.() || `m_${Date.now()}`;
        const userMessage = {
          id: Date.now(),
          ws_message_id: messageId,
          isResponse: false,
          timestamp: now.toISOString(),
          sender:
            (this.session.firstname || "").trim() ||
            (this.session.username || "").trim() ||
            "user",
          text: message,
        };
        this.session.addCurrentChatHistoryEntry(userMessage);
        await this.$nextTick();
        this.scrollToBottom();

        this.sendTextMessageViaWs(message, messageId);
        await this.$nextTick();
        this.scrollToBottom();
        // waiting = false is handled by the assistant_done WS event
      } catch (error) {
        console.error("Error sending message:", error);
        this.waiting = false;
      }
    },
    renderMarkdown(raw = "") {
      // 1.  Escape HTML
      let html = raw
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      // 2.  Lists  (each "- item" on its own line)
      html = html
        .replace(/(?:^|\n)- (.*?)(?=\n|$)/g, "\n<li>$1</li>") // bullets → <li>
        .replace(/(<li>[\s\S]*<\/li>)/g, "<ul>$1</ul>"); // wrap list

      // 3.  Inline styles
      html = html
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // bold
        .replace(/\*(.*?)\*/g, "<em>$1</em>") // italic
        .replace(/`([^`]+)`/g, "<code>$1</code>"); // code
      // .replace(/$begin:math:display$(.*?)$end:math:display$$begin:math:text$(https?:\\/\\/[^\\s]+?)$end:math:text$/g,
      // '<a href="$2" target="_blank">$1</a>');        // links

      // 4.  Breaks
      html = html.replace(/(?:\r\n|\r|\n)/g, "<br>");

      // 5. Links (detect URLs)
      html = html.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" style="color:#1a0dab; text-decoration:underline;">$1</a>',
      );

      return html.trim();
    },
  },
};
</script>

<style scoped>
.loading-message {
  text-align: center;
  font-size: 1.2em;
  color: #666;
}

.chat-history {
  flex: 1;
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
  padding-top: 15px;
  border-radius: 25px;
  margin-bottom: 10px;
}

.chat-summary-display {
  white-space: pre-wrap;
  /* Match the conversation panel (.chat-window): translucent white so each
     group's themed background tints it the same way, instead of solid white. */
  background-color: #ffffffb2;
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 10px;
  width: 100%;
  font-size: 1em;
  font-family: "Roboto", sans-serif;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.summary-scroll {
  position: static !important; /* overrides any global sticky/fixed */
  top: auto !important;
}

.chat-window {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: auto;
  width: 95%;
  padding: 10px;
  overflow-y: visible;
  background: #ffffffb2;
  border-radius: 25px;
}

.chat-message {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
}

/* Styles for text input messages */
.text-input {
  padding: 10px;
  border-radius: 10px;
  /* margin-bottom: 10px; */
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
}

/* Styles for response messages */
.response {
  padding: 10px;
  border-radius: 10px;
  /* margin-bottom: 10px; */
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
}

/* Styles for the message header */
.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.message-logo {
  height: 20px;
  margin-right: 10px;
}

.message-sender {
  font-weight: bold;
  margin-right: 10px;
  margin-bottom: 5px;
  margin-top: 10px;
}

.message-content {
  display: flex;
  flex-direction: column;
}

.message-text {
  display: flex;
  align-items: baseline;
}

.timestamp {
  color: gray;
  font-size: 0.8em;
  margin-right: 5px;
  margin-bottom: 5px;
}

.sender {
  font-weight: bold;
  display: inline;
}

.text {
  font-weight: normal;
  margin-left: 5px;
  display: inline;
  white-space: pre-wrap;
}
/*
  A failed turn. Keeps the ordinary bubble layout so spacing, sizing and
  contrast come from rules that already work, and is set apart by its speaker
  rather than by a bespoke shape.

  Sized to match .coach-icon so the avatar column does not jump between rows.
  Fixed colours rather than theme variables, the same choice .timestamp makes:
  --secondary-color is #475569 in the navy theme but #f7f7f7 in the civil one,
  so anything tinted with it was near-white on a light background.
*/
.system-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 2px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.07);
  color: #5a5f66;
  font-size: 1.1rem;
}

.system-notice .message-sender {
  color: #5a5f66;
}

.system-notice .message-text .text {
  color: #4a4a4a;
  font-style: italic;
}

/*
  Sits directly above the composer, where the reply would have appeared.
  Amber rather than red: the session is interrupted, not broken, and the
  participant did nothing wrong.
*/
.server-down-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  /* Same inset and gap cancellation as the maintenance banner, for the same
     reasons (see the note there): these two occupy the same slot and can appear
     in the same session, so they have to agree with each other as well as with
     the composer. */
  margin: -12px 12px -12px;
  max-width: calc(100% - 24px);
  border: 1px solid #f0c36d;
  border-radius: 12px;
  background-color: #fdf6e3;
  color: #7a5b12;
  font-size: 0.9em;
  line-height: 1.4;
}

.server-down-banner__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0a300;
}

/*
  Maintenance. Fixed colours rather than theme variables, for the reason the
  System bubble uses them: the civilian theme's --secondary-color is #f7f7f7,
  which rendered near-white on the light background and made the notice
  unreadable in exactly the theme most participants use.

  Two visual weights. The approaching notice is informational blue and easy to
  ignore; the in-progress one is the same slate as the System bubble, because at
  that point it is describing a state the participant cannot act around.
*/
.maintenance-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  /*
    Inset to match .text-prompt's own 12px padding, so the banner's edges line
    up with the textarea and the Send button directly below it. Without this it
    spanned the full container and visibly overhung the chat panel on both
    sides.

    The composer is the right reference rather than the chat panel: the panel's
    right edge shifts by the scrollbar width depending on whether the
    conversation overflows, so aligning to it would only look correct half the
    time.

    max-width rather than width, and that distinction is load-bearing. The
    parent is a flex column, so the banner is stretched to the container's cross
    size; margins alone did not reduce it, and neither did `width`, which the
    stretch overrode -- the banner kept full width and overflowed 12px FURTHER
    right than before, clipped by the parent's overflow-x: hidden. The stretch
    step has to respect min/max constraints, so max-width clamps it where width
    did not. Measured: lands on 182.1 and 1716, matching the textarea and the
    Send button exactly.

    The negative VERTICAL margins cancel the parent's flex `gap: 12px`, which
    applies between every child of .new-main-page and is most of why the banner
    sat 22px below the conversation and 32px above the textarea. Cancelling it
    here rather than lowering the parent's gap keeps the change scoped to the
    case where a banner is present; with no banner the layout is untouched.
  */
  margin: -12px 12px -12px;
  max-width: calc(100% - 24px);
  border: 1px solid #b8d4ea;
  border-radius: 12px;
  background-color: #eef6fc;
  color: #2b5a7d;
  font-size: 0.9em;
  line-height: 1.4;
}

.maintenance-banner--blocked {
  border-color: rgba(0, 0, 0, 0.12);
  background-color: #f1f2f4;
  color: #4a4a4a;
}

.maintenance-banner i {
  flex: 0 0 auto;
  font-size: 1.05em;
}

/*
  Scoped to the banner. The pending CARD is a centred column, and a
  margin-left:auto on the shared button class beat its align-items and pinned
  the button to the right edge.
*/
.maintenance-banner .maintenance-retry {
  margin-left: auto;
}

.maintenance-retry {
  flex: 0 0 auto;
  padding: 4px 12px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 0.9em;
  cursor: pointer;
}

.maintenance-retry:disabled {
  opacity: 0.55;
  cursor: default;
}

.maintenance-pending {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 460px;
  margin: 0 auto;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  background-color: #f1f2f4;
  color: #4a4a4a;
  text-align: center;
}

.maintenance-pending i {
  font-size: 1.6em;
}

.maintenance-pending p {
  margin: 0;
  line-height: 1.5;
}

.text-prompt {
  display: flex;
  align-items: center;
  margin-bottom: 2%;
}

/*
  With a banner directly above, the composer's own 12px top padding is the last
  thing separating the two, and it reads as a gap rather than as breathing room.
  Trimmed only in that case, so the composer keeps its normal padding the rest
  of the time.

  Doubled selector for specificity, not by accident. `.text-prompt` is re-declared
  with a `padding` shorthand inside both breakpoints further down this file, and
  at equal specificity the later rule wins -- a single-class modifier here would
  be silently overridden at every viewport width.
*/
.text-prompt.text-prompt--under-banner {
  padding-top: 4px;
}

.text-prompt input,
.text-prompt textarea {
  flex: 1;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 20px;
  margin-right: 10px;
}
.text-prompt textarea {
  height: 4em;
  max-height: 8em;
  border-radius: 1em;
}

.text-prompt-controls {
  flex: 0;
}

.text-prompt button {
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
  min-width: 5em;
}

/*
  A disabled Send has to LOOK disabled. Without this it kept the full primary
  colour and the pointer cursor while maintenance was in progress, so the only
  way to discover it did nothing was to click it -- which reads as the page
  being broken rather than as a scheduled hold.
*/
.text-prompt button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/*
  The waiting spinner is an active in-progress indicator, not an inert
  disabled control, so it's exempted from the disabled-button fade above --
  it should read as "working," not "unavailable."
*/
.text-prompt button.waiting-button:disabled {
  opacity: 1;
}

.text-prompt button svg {
  fill: currentColor;
}

/*
  loading.svg fills each spoke with currentColor, which only resolves for an
  inlined SVG -- loaded here via <img>, it silently defaults to black instead.
  brightness(0) forces every filled pixel to solid black without touching the
  alpha channel (so the spokes' built-in per-spoke opacity gradient survives),
  then invert(1) flips that to solid white. Deliberately theme-independent:
  --primary-color (this button's background) is a mid grey in the civil theme
  and a dark navy in the navy theme, and white has good contrast against both,
  so this does not need a per-theme variant.
*/
.waiting-button .svg {
  filter: brightness(0) invert(1);
}

.text-prompt-note {
  color: gray;
  font-size: 0.8em;
}

.header-image {
  max-width: 100%;
  height: auto;
  max-height: 168px;
  object-fit: contain;
  justify-self: center;
}

.new-main-page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  gap: 12px; /* adds space between header and chat */
}

.new-main-page h1,
.new-main-page p,
.new-main-page form,
.new-main-page div {
  width: 100%;
  text-align: left;
}

.new-main-header {
  display: grid;
  grid-template-columns: 11fr 1fr;
  align-items: center;
  gap: 12px;
  padding: 0;
  margin: 0;
  max-width: 100%;
}

.profile-photo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 2px;
}

.bi-person-fill,
.bi-person-circle {
  font-size: 35px;
  width: 40px;
  height: 40px;
  margin-right: 2px;
}

.coach-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%; /* keep it circular to match user avatar */
  margin-right: 2px;
  object-fit: cover; /* keeps it nicely contained if source is not square */
}

.empty-chat-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 85%;
  width: 95%;
  padding: 20px;
  background: #ffffffb2;
  border-radius: 25px;
  margin-bottom: 10px;
}

.empty-chat-container p {
  font-size: 1.2em;
  color: #666;
  text-align: center;
}
.centered-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 20px;
}

.create-session-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 85%;
  width: 95%;
  padding: 20px;
  background: #ffffffb2;
  border-radius: 25px;
  margin-bottom: 10px;
}

.create-session-content {
  text-align: center;
  max-width: 500px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.create-session-content h3 {
  font-size: 1.8em;
  color: #333;
  margin-bottom: 20px;
}

.create-session-content p {
  font-size: 1.2em;
  color: #666;
  margin-bottom: 30px;
  text-align: center;
}

.create-session-button {
  padding: 16px 32px;
  font-size: 1.2em;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.create-session-button:hover {
  background-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

.create-session-button:active {
  transform: translateY(0);
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 85%;
  width: 95%;
  padding: 20px;
  background: #ffffffb2;
  border-radius: 25px;
  margin-bottom: 10px;
}

.loading-container p {
  font-size: 1.2em;
  color: #666;
  text-align: center;
}

.survey-ack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.survey-ack-btn {
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
}
.survey-ack-btn:hover {
  background-color: var(--primary-color);
  filter: brightness(0.95);
}
.survey-ack-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.empty-chat .welcome-prompt {
  font-size: 1.2em;
  color: #666;
  text-align: center;
  padding: 20px;
  margin-top: 20px;
}

.voice-recording-overlay {
  position: absolute;
  top: 50%;
  left: 12px;
  right: 200px; /* ADD - prevents overflow into button area */
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 10;
  pointer-events: none;
  max-width: calc(
    100% - 220px
  ); /* ADD - ensures it doesn't exceed available space */
}

.microphone-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0; /* ADD - prevents icon from shrinking */
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: mic-pulse 1.5s ease-in-out infinite;
}

.microphone-icon svg {
  width: 100%;
  height: 100%;
}

/* Waveform Container */
.waveform-container {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 40px;
  flex: 1; /* ADD - allows it to fill available space */
  max-width: 100%; /* ADD - prevents overflow */
  overflow: hidden; /* ADD - hides any bars that would overflow */
}

.waveform-bar {
  width: 3px;
  flex-shrink: 0; /* ADD - prevents bars from compressing */
  background-color: #000;
  border-radius: 2px;
  animation: waveform-animate 1.5s ease-in-out infinite;
  min-height: 4px;
}
/* Waveform Animation */
@keyframes waveform-animate {
  0%,
  100% {
    height: 8px;
  }
  50% {
    height: 40px;
  }
}

@keyframes mic-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

/* Voice Mode Toggle - matches DebugPanel.vue styling */
/* Voice Mode row inside the card: label on the left, toggle on the right,
   both vertically centered so they read as aligned. */
.voice-mode-toggle {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* Right column of the header grid: groups the Voice Mode toggle and the
   demo-only End session control into one subtle card. */
.header-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 8px 10px;
  /* Keep the card off the header's right edge (mirrors its 12px left gap). */
  margin-right: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.45);
}

/* Defeat the global `.new-main-page div { width: 100% }` for this card, so it
   is sized to its grid track and the right margin above actually opens a gap
   instead of overflowing the track. */
.new-main-page .header-controls {
  width: auto;
}

/* Hairline separating the two rows of the card. */
.control-divider {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
}

/* Subtle by default, matching the Voice Mode label; fills red on hover to
   signal the session-destructive (demo) action. */
.end-session-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 5px 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: #333;
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;
}

.end-session-button:hover:not(:disabled) {
  color: #fff;
  background-color: #b42318;
  border-color: #b42318;
}

.end-session-button:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Wraps the chat history so the summarizing overlay can cover exactly the
   chat panel (between the header and the input). */
.chat-panel {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Demo-only translucent overlay shown while the session summary generates
   after a force-end (the input is hidden then, so this is the progress signal). */
.summarizing-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border-radius: 25px;
  z-index: 5;
}

/* width:auto / text-align:center override the global .new-main-page div/p rules. */
.summarizing-overlay .summarizing-card {
  width: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px 34px;
  background-color: #ffffff;
  border-radius: 14px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.18);
}

.summarizing-spinner {
  width: 40px;
  height: 40px;
}

.summarizing-overlay .summarizing-note {
  width: auto;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  color: #334155;
}

/* Demo-only banner shown when summaries are not ready after a force-end. */
.summary-pending-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 12px;
  margin: 0 8px;
}

.summary-pending-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  color: var(--secondary-color, #475569);
}

.start-anyway-button {
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background-color: var(--primary-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.start-anyway-button:hover {
  background-color: var(--primary-color);
  opacity: 0.9;
}

.voice-toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
  align-items: center;
  white-space: nowrap;
}

.voice-label-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.voice-browser-tooltip-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #aaa;
  font-size: 13px;
  line-height: 1;
  display: flex;
  align-items: center;
}

.voice-browser-tooltip-btn:hover {
  color: #666;
}

/* iOS-style switch - matches DebugPanel */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
  /* In the flex control row, never let the track compress below 50px, or the
     knob's fixed translateX would slide past the shrunken track edge. */
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch input:disabled + .slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 28px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--primary-color);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--primary-color);
}

input:checked + .slider:before {
  transform: translateX(22px);
}

.slider.round {
  border-radius: 28px;
}

.slider.round:before {
  border-radius: 50%;
}

/* Helper Text */
.helper-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  text-align: right;
}

/* Text Input - Disabled in Voice Mode */
textarea.voice-mode-disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Push-to-Talk Button */
.push-to-talk-button {
  position: relative;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  touch-action: none; /* prevent Android from interpreting hold as gesture */
  -webkit-touch-callout: none; /* disable iOS callout */
  user-select: none; /* prevent text selection on long press */
  background: linear-gradient(
    135deg,
    #dc3545 0%,
    #c82333 100%
  ); /* CHANGE to red gradient */
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
  min-width: 180px;
}

.push-to-talk-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4); /* CHANGE to red shadow */
}

.push-to-talk-button:active:not(:disabled) {
  transform: translateY(0);
}

.push-to-talk-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.push-to-talk-button.recording {
  background: linear-gradient(
    135deg,
    #ff4757 0%,
    #dc3545 100%
  ); /* BRIGHTER red gradient */
  animation: pulse-glow 1.5s ease-in-out infinite;
}

.record-button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* Record Circle Indicator */
.record-circle {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.record-circle.active {
  border-color: white;
  animation: record-pulse 1.5s ease-in-out infinite;
}

.record-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: white;
  transition: all 0.3s ease;
}

.record-circle.active .record-dot {
  animation: dot-blink 1.5s ease-in-out infinite;
}

.record-text {
  font-size: 14px;
  letter-spacing: 0.5px;
}

.bubble-actions {
  display: inline-flex;
  margin-left: 7px;
  gap: 10px;
  margin-top: 6px;
  align-self: flex-start;

  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bubble-action-btn {
  width: 12px;
  height: 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(255, 255, 255, 0.8);
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: #333;
  padding: 0;
}

.bubble-action-btn:hover {
  filter: brightness(0.97);
}

.bubble-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.bubble-action-btn i {
  font-size: 14px;
  line-height: 1;
}

.bubble-action-btn i.bi-check2 {
  color: #198754;
}

/* Reveal actions on hover (desktop) */
.text-input:hover .bubble-actions,
.response:hover .bubble-actions {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

/* Animations */
@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 20px rgba(220, 53, 69, 0.6), 0 0 40px rgba(220, 53, 69, 0.4);
  }
  50% {
    box-shadow: 0 0 30px rgba(220, 53, 69, 0.8), 0 0 60px rgba(220, 53, 69, 0.6);
  }
}

@keyframes record-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes dot-blink {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.9);
  }
}

/* Desktop styles - BEFORE mobile media query */
@media (min-width: 769px) {
  .text-prompt {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    padding: 12px;
    position: relative;
  }

  .helper-text {
    position: absolute;
    top: -20px;
    right: 12px;
    font-size: 12px;
    color: #666;
  }

  .text-prompt textarea {
    flex: 1;
    margin-right: 0;
    height: 60px;
    min-height: 60px;
  }

  .text-prompt-controls {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 4px;
    /* flex-shrink: 0; */
  }

  .text-prompt button {
    min-width: auto;
    height: auto;
  }

  /* Specific height for push-to-talk button */
  .push-to-talk-button {
    min-width: 60px;
    height: 40px;
    padding: 8px 16px; /* REDUCE padding to fit content */
    display: flex; /* ADD to control internal layout */
    align-items: center; /* ADD to center content vertically */
    justify-content: center; /* ADD to center content horizontally */
  }

  /* Make send button same size */
  .send-button {
    height: 40px;
    min-width: 90px;
  }

  /* Adjust button content for smaller height */
  .push-to-talk-button .record-button-content {
    gap: 8px; /* REDUCE from 12px */
  }

  .push-to-talk-button .record-circle {
    width: 20px; /* REDUCE from 24px */
    height: 20px;
  }

  .push-to-talk-button .record-dot {
    width: 10px; /* REDUCE from 12px */
    height: 10px;
  }

  .push-to-talk-button .record-text {
    font-size: 13px;
    line-height: 1.2; /* ADD to prevent overflow */
  }
  /* Desktop waveform adjustments */
  .voice-recording-overlay {
    left: 20px;
    right: 220px; /* UPDATE - more space for desktop button */
    max-width: calc(100% - 240px);
  }

  .microphone-icon {
    width: 36px;
    height: 36px;
  }

  .waveform-container {
    gap: 4px;
  }

  .waveform-bar {
    width: 4px;
  }
}

@media (max-width: 768px) {
  /* Define vars on a component ancestor that actually exists in the template */
  .new-main-page {
    --composer-h: 64px;
    --edge-gutter: 6px;
  }
  .new-main-header {
    gap: 8px;
    padding: 0 8px;
  }

  .voice-mode-toggle {
    gap: 8px;
  }

  .header-controls {
    gap: 6px;
    padding: 6px 8px;
  }

  .end-session-button {
    font-size: 13px;
    padding: 4px 8px;
  }

  /* Kill default <ul> padding/margins so messages can sit left */
  ul.chat-message {
    list-style: none;
    margin: 0;
    padding-left: 0;
    padding-right: 0;
  }
  .chat-history {
    padding-left: 2px;
    padding-right: 2px;
  }
  .chat-window {
    width: 100%; /* override base 95% */
    border-radius: 10px;
  }
  .chat-message {
    align-items: stretch;
  }

  .text-input,
  .response {
    width: 100%;
    padding: 6px 8px;
    margin-bottom: 8px;
  }
  .message-header {
    gap: 6px;
    margin-bottom: 4px;
    margin-left: -2px; /* optional tiny nudge further left */
  }
  .profile-photo,
  .coach-icon,
  .bi-person-circle {
    margin-right: 6px;
  }
  .message-text .text {
    margin-left: 2px;
  }

  /* 9:1 composer, using the vars that now exist */
  .text-prompt {
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: grid;
    grid-template-columns: 9fr 1fr;
    gap: 8px;
    align-items: stretch;
    background: var(--background-color);
    /* padding: 8px 10px env(safe-area-inset-bottom, 0); */
    padding: 8px 2px env(safe-area-inset-bottom, 0);
  }

  /*
    Track the composer's own horizontal padding, which drops from 12px to 2px
    here. Both banners sit directly above it and are aligned to its controls
    rather than to the chat panel, so the inset has to follow it across the
    breakpoint or the fix for desktop becomes a 10px mismatch on mobile.
  */
  .maintenance-banner,
  .server-down-banner {
    /* Horizontal inset tracks the composer's 2px padding here; the vertical
       values must repeat the base rule's gap cancellation, since this shorthand
       would otherwise reset it and the tightened spacing would apply on desktop
       only. */
    margin: -12px 2px -12px;
    max-width: calc(100% - 4px);
  }
  .text-prompt textarea {
    margin-right: 0;
    height: var(--composer-h);
    max-height: var(--composer-h);
    resize: none;
    border-radius: 12px;
    line-height: 1.2;
  }
  .text-prompt-controls {
    display: contents;
  }
  .text-prompt button:not(.push-to-talk-button) {
    width: 100%;
    height: var(--composer-h);
    padding: 0;
    border-radius: 12px;
    font-size: 14px;
  }
  .text-prompt-note {
    display: none;
  }

  .voice-toggle-label {
    font-size: 12px;
  }

  .switch {
    width: 44px;
    height: 24px;
  }

  .slider:before {
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
  }

  input:checked + .slider:before {
    transform: translateX(20px);
  }

  .push-to-talk-button {
    width: 100%;
    height: var(--composer-h);
    padding: 12px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  /* Mobile voice mode: textarea hidden, PTT button fills full width */
  .text-prompt--voice {
    grid-template-columns: 1fr;
  }
  .text-prompt--voice .voice-recording-overlay {
    right: 0;
    max-width: 100%;
  }

  .record-circle {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
  }

  .record-dot {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .voice-recording-overlay {
    left: 8px;
    right: 150px; /* ADD - space for mobile button */
    max-width: calc(100% - 160px);
    gap: 8px;
  }

  .microphone-icon {
    width: 24px;
    height: 24px;
  }

  .waveform-container {
    gap: 2px;
    height: 30px;
  }

  .waveform-bar {
    width: 2px;
  }

  .bubble-actions {
    opacity: 1;
    pointer-events: auto;
    transform: none;
  }

  @keyframes waveform-animate {
    0%,
    100% {
      height: 6px;
    }
    50% {
      height: 30px;
    }
  }
}

/* 1) Keep widths inside the viewport */
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* 2) Kill horizontal scroll at the root level */
:global(html, body, #app) {
  max-width: 100vw;
  overflow-x: hidden;
}

/* 3) Also clamp the page + scrollers (belt & suspenders) */
.new-main-page,
.chat-history {
  max-width: 100%;
  overflow-x: hidden;
}

/* 4) Make sure primary blocks can't exceed the viewport width */
.new-main-header,
.chat-window,
.text-prompt {
  max-width: 100%;
}

/* 5) Prevent long links / words from forcing overflow in messages */
.message-text .text {
  overflow-wrap: anywhere; /* modern */
  word-break: break-word; /* fallback */
}

/* 6) Ensure images never push the layout wider */
.header-image,
.coach-icon,
.profile-photo {
  max-width: 100%;
  height: auto;
  display: block;
}
</style>
